// ============================================
// PREVISUALIZADOR.JS — CO-2 / CO-2.A / CO-2.C: editor de cotizacion
// ============================================
// Carga GET /api/tienda/carritos/:id, permite editar lineas (cantidad, desc%),
// nombrar la cotizacion, vincular UN expediente (modal buscar+elegir, opcional),
// ver el desglose en cascada hasta UTILIDAD NETA y guardar via POST /generar.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, FileText, CheckCircle2, Trash2, Package, Link2, Download, Plus, X, Clock, RotateCcw, AlertTriangle, Copy, ExternalLink, Layers, Snowflake, PackagePlus, GripVertical, Repeat, Landmark } from 'lucide-react';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useSensoresDnD } from './components/PersonalizarMenu';
import { tokens } from './styles/tokens';
import { generarCotizacionPDF, EMISOR } from './pdf/cotizacionPDF';
import { QRCodeCanvas } from 'qrcode.react';
import { fmtGarantiaTexto } from './components/GarantiasLista';
import {
  API_BASE_CLI, PP, COLOR_OSCURO, COLOR_FONDO, COLOR_GRIS,
  COLOR_GRIS_CLARO, COLOR_BORDE, estiloInput, estiloBotonPrimario, estiloBotonSecundario, estiloTarjeta,
} from './components/helpersClientes';
import { useMoneda } from './components/SelectorMoneda';
import { useUsuario } from './context/UsuarioContext';
import { useResponsive, useAnchoViewport } from './hooks/useResponsive';
import { leerClienteStorage } from './components/SelectorCliente';
import SelectorIconoLucide, { IconoLucide } from './components/SelectorIconoLucide';
import ModalOrigenProducto from './components/ModalOrigenProducto';
import PaginaProducto from './PaginaProducto';
import ModalColeccionOrigen from './components/ModalColeccionOrigen';
import ModalCartaBancaria from './components/ModalCartaBancaria';
import TerminosCotizacion from './components/TerminosCotizacion';
import { volverACotizacion } from './components/CarritoFlotante';
import { activarModoAgregar } from './utils/modoAgregarCotizacion';
import { useIvaModo, ToggleIva, conIvaSegun, quitarIvaSegun } from './components/SelectorIva';
import { TASA_IVA } from './config/iva';
import { authHeaders } from './utils/storage';
// Primitivos de edicion de precio/margen por linea (extraidos para reuso en Colecciones).
import { round2, margenDePrecioCosto, InputPrecio, InputMargen } from './components/LineaPrecioEditable';
import { ligaPublica } from './config/api';

// 'servicio' y 'cable' cuentan como instalacion / mano de obra; 'producto' como proveedores.
const esInstalacion = (tipoCat) => tipoCat === 'servicio' || tipoCat === 'cable';

// Etiqueta de origen del articulo a partir de la fuente cruda (syscom_producto / beep_producto /
// beep_fijo / ...). Devuelve 'Syscom' | 'Beep' | null (null si la cotizacion vieja no la persistio).
function origenLabel(fuente) {
  if (!fuente) return null;
  const f = String(fuente).toLowerCase();
  if (f.includes('syscom')) return 'Syscom';
  if (f.includes('beep')) return 'Beep';
  return null;
}

// FICHA-PRODUCTO: bloque imagen + nombre de una linea. Si la linea tiene ficha en
// La Tienda (l.id_unico), es clickeable y abre la ficha completa; si no (servicios,
// cables, manuales), se muestra sin clic. Mismo bloque para tabla (thumb 64) y
// tarjeta compacta (thumb 56). Affordance: nombre en azul + icono ExternalLink,
// cursor pointer, hover suave, area tactil >=44px.
function ProductoInfoClickeable({ l, thumbSize, inst, onVer }) {
  const clickeable = !!l.id_unico;
  const compacto = thumbSize <= 56;
  const gap = compacto ? 10 : 12;
  const info = (
    <div style={{ minWidth: 0, flex: compacto ? 1 : undefined, maxWidth: compacto ? undefined : 320 }}>
      <div style={{
        fontWeight: compacto ? 700 : 600, fontSize: compacto ? 14 : undefined,
        lineHeight: compacto ? 1.25 : 1.3, whiteSpace: 'normal', wordBreak: 'break-word',
        color: clickeable ? tokens.primary : COLOR_OSCURO,
      }}>
        {l.nombre}
        {clickeable && <ExternalLink size={13} strokeWidth={2.5} style={{ marginLeft: 5, verticalAlign: '-2px', opacity: 0.75 }} />}
      </div>
      {l.modelo && (
        <div style={{ fontSize: compacto ? 12 : 13, color: '#6d28d9', fontWeight: 800, fontFamily: 'monospace', marginTop: 1 }}>{l.modelo}</div>
      )}
      <div style={{ fontSize: 11, color: inst ? tokens.primary : COLOR_GRIS_CLARO, fontWeight: inst ? 700 : (compacto ? 500 : 400) }}>
        {inst ? 'Instalacion' : (origenLabel(l.fuente) || 'Producto')}
      </div>
    </div>
  );
  if (!clickeable) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap }}>
        <Thumb url={l.imagen_url} size={thumbSize} />{info}
      </div>
    );
  }
  return (
    <div
      role="button" tabIndex={0}
      onClick={() => onVer(l)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onVer(l); } }}
      title="Ver ficha del producto (specs, fotos, descripcion)"
      style={{
        display: 'flex', alignItems: 'center', gap, cursor: 'pointer',
        borderRadius: 8, padding: 4, margin: -4, minHeight: 44, transition: 'background 0.15s',
        WebkitTapHighlightColor: 'transparent',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = '#eef4fb'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >
      <Thumb url={l.imagen_url} size={thumbSize} />{info}
    </div>
  );
}

// CO-ESTADOS: colores del chip de estado de la COTIZACION (mismos que la lista).
// 'vencida' es DERIVADO (estado_efectivo): se muestra pero no se elige.
const ESTADO_COLOR = {
  borrador:   { bg: '#f3f4f4', color: '#5e666e', label: 'Borrador' },
  enviada:    { bg: '#e6f1fb', color: '#2f5f9e', label: 'Enviada' },
  autorizada: { bg: '#e8faf5', color: '#1D9E75', label: 'Autorizada' },
  rechazada:  { bg: '#fde8e8', color: '#e05c5c', label: 'Rechazada' },
  vencida:    { bg: '#fff3e8', color: '#9a3412', label: 'Vencida' },
};
// Estados que SE PUEDEN elegir (vencida queda fuera: es automatica).
const ESTADOS_OPCIONES = [
  { id: 'borrador',   label: 'Borrador' },
  { id: 'enviada',    label: 'Enviada' },
  { id: 'autorizada', label: 'Autorizada' },
  { id: 'rechazada',  label: 'Rechazada' },
];

// round2, colorMargen y margenDePrecioCosto viven ahora en
// components/LineaPrecioEditable.js (importados arriba) — mismo codigo, reusado por
// el precotizador y por Colecciones.

// DnD #4a: ORDEN 100% MANUAL. Antes reagrupaba productos arriba / instalaciones al final;
// ahora se respeta el orden que llega (GET ORDER BY orden ASC) y el que deja el usuario al
// arrastrar. Identidad (se conserva la funcion porque la usan varios callers al cargar).
function ordenarLineas(arr) {
  return arr;
}

// DnD #4a: fila de tabla arrastrable (desktop). Solo el HANDLE (≡) arrastra; el resto de la
// fila sigue editable (cantidad/margen/precio). Mobile-safe via los sensores con delay.
function SortableRowCotizador({ id, inst, bloqueado, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled: !!bloqueado });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    background: isDragging ? '#eef4fb' : (inst ? tokens.primarySoft : undefined),
    opacity: isDragging ? 0.9 : 1,
    position: isDragging ? 'relative' : undefined,
    zIndex: isDragging ? 5 : undefined,
  };
  return (
    <tr ref={setNodeRef} style={style} {...attributes}>
      <td style={{ borderTop: `1px solid ${COLOR_BORDE}`, padding: '8px 2px', textAlign: 'center', verticalAlign: 'middle', width: 28 }}>
        {/* SPRINT DINERO 1: sin listeners cuando esta bloqueado (autorizada) — el
            gesto de arrastre no debe "vivir" en una vista de solo lectura. */}
        <span {...(bloqueado ? {} : listeners)} title={bloqueado ? undefined : 'Arrastrar para reordenar'}
          style={{ display: 'inline-flex', cursor: bloqueado ? 'default' : 'grab', color: COLOR_GRIS_CLARO, opacity: bloqueado ? 0.35 : 1, touchAction: 'none', userSelect: 'none' }}>
          <GripVertical size={16} />
        </span>
      </td>
      {children}
    </tr>
  );
}

// DnD #4a: tarjeta arrastrable (compacto/celular). Handle (≡) a la izquierda; el resto editable.
function SortableCardCotizador({ id, inst, bloqueado, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled: !!bloqueado });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.85 : 1,
    position: 'relative', zIndex: isDragging ? 5 : undefined,
    display: 'flex', alignItems: 'stretch', gap: 6,
    border: `1px solid ${inst ? tokens.primary : COLOR_BORDE}`, borderRadius: 12,
    background: inst ? tokens.primarySoft : '#fff',
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <div {...(bloqueado ? {} : listeners)} title={bloqueado ? undefined : 'Mantén presionado para arrastrar'}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 2px',
          cursor: bloqueado ? 'default' : 'grab', color: COLOR_GRIS_CLARO, opacity: bloqueado ? 0.35 : 1,
          touchAction: 'none', userSelect: 'none', flexShrink: 0,
        }}>
        <GripVertical size={18} />
      </div>
      <div style={{ flex: 1, minWidth: 0, padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {children}
      </div>
    </div>
  );
}

export default function Previsualizador({ carritoId, cotizacionId, volver }) {
  const { tc, convertir } = useMoneda();
  const { usuario, puede, accesoTotal, permisosFetched } = useUsuario();
  const { isMobile } = useResponsive();
  // RESPONSIVE B-2: la tabla de articulos es ancha (8 columnas). Se muestra SOLO cuando
  // hay ancho real (>=1100); por debajo (celular, tablet y PC VERTICAL ~1080) se usan las
  // tarjetas apiladas (sin scroll horizontal). `isMobile` (<768) se sigue usando para
  // ajustes finos (ToggleIva, direccion de las tarjetas de totales).
  const ancho = useAnchoViewport();
  const compacto = ancho < 1100;
  // CO-ESTADOS: gating del control de estado. Deny-by-default mientras cargan permisos;
  // direccion (accesoTotal) siempre puede.
  const puedeCambiarEstado = accesoTotal || (permisosFetched && puede('cotizaciones.cambiar-estado'));
  // Switch compartido Con IVA / Sin IVA (solo afecta lo MOSTRADO; se guarda sin IVA).
  const [ivaModo, setIvaModo] = useIvaModo();

  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [lineas, setLineas] = useState([]);              // estado LOCAL editable
  const [nombre, setNombre] = useState('');
  // FICHA-PRODUCTO: id_unico del producto cuya ficha se ve como capa encima del
  // editor (o null). No desmonta el cotizador -> al cerrar se vuelve al mismo punto.
  const [fichaUnico, setFichaUnico] = useState(null);
  const verFicha = useCallback((l) => { if (l && l.id_unico) setFichaUnico(l.id_unico); }, []);
  // Cerrar la ficha con la tecla Escape (atajo natural de escritorio); solo activo mientras
  // la capa esta abierta. El boton "Volver a la cotizacion" sigue siendo la via principal.
  useEffect(() => {
    if (!fichaUnico) return undefined;
    const onEsc = (e) => { if (e.key === 'Escape') setFichaUnico(null); };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [fichaUnico]);
  const [nota, setNota] = useState('');                  // notas_cliente
  const [monedaCot, setMonedaCot] = useState('MXN');     // moneda de PRESENTACION (datos en MXN)
  // DOCUMENTO DE 3 PAGINAS (2026-09-07): condiciones de pago y switches. Informativos, no
  // tocan montos. Se guardan con la cotizacion (PUT/POST) y salen en las paginas 2 y 3.
  const [anticipoPct, setAnticipoPct] = useState(100);
  const [anticipoOtro, setAnticipoOtro] = useState('');
  // TERMINOS-COTIZACION (2026-10-12): cuando se paga el SALDO. null = contra entrega; 15 / 30 = dias;
  // -1 = "Otro" (dias a mano en saldoOtro). Solo cuenta si el anticipo es menor al 100%.
  const [saldoModo, setSaldoModo] = useState(null);
  const [saldoOtro, setSaldoOtro] = useState('');
  const [tiempoEntrega, setTiempoEntrega] = useState('');
  const [incluirPago, setIncluirPago] = useState(true);
  const [incluirCert, setIncluirCert] = useState(true);
  // PDF-DESCRIPCION: descripcion del producto debajo de su nombre. `descripcionDefinida` = la eleccion ya es de ESTA
  // cotizacion (la trajo el servidor o la toco el vendedor); si no, al guardar se manda null y el servidor hereda la
  // ultima eleccion para ese cliente.
  const [mostrarDescripcion, setMostrarDescripcion] = useState(false);
  const [descripcionDefinida, setDescripcionDefinida] = useState(false);
  const [cuentasSel, setCuentasSel] = useState(null);     // null = principal por default
  const [empresa, setEmpresa] = useState(null);           // GET /api/empresa (emisor, cuentas, casos)
  const [garantiasLinea, setGarantiasLinea] = useState({}); // id_linea -> {garantia_meses, garantia_dias, garantia_origen}
  const [certInfo, setCertInfo] = useState(null);         // {folio, codigo} tras generar el PDF
  // TERMINOS-COTIZACION (2026-10-05): catalogo de clausulas, la FOTO guardada en la cotizacion y
  // lo marcado ahora. selTerminos === null = cotizacion anterior a la funcion (no se le añade
  // nada por detras). El texto que vale es el que devuelve el servidor al guardar.
  const [catClausulas, setCatClausulas] = useState([]);
  const [familiasCat, setFamiliasCat] = useState([]);
  const [fotoTerminos, setFotoTerminos] = useState(null);
  const [selTerminos, setSelTerminos] = useState(null);
  const [terminosTocados, setTerminosTocados] = useState(false);
  const [familiasSugeridas, setFamiliasSugeridas] = useState([]);
  const [cargandoTerminos, setCargandoTerminos] = useState(false);
  const [duplicadaDeFolio, setDuplicadaDeFolio] = useState(null);
  const [vigenciaDias, setVigenciaDias] = useState(15);
  const [generandoPDF, setGenerandoPDF] = useState(false);
  const qrRef = useRef(null);

  useEffect(() => {
    fetch(`${API_BASE_CLI}/api/empresa`, { headers: authHeaders() })
      .then(r => (r.ok ? r.json() : null))
      .then(j => { if (j && j.ok) setEmpresa(j); })
      .catch(() => {});
  }, []);
  const [expediente, setExpediente] = useState(null);    // { id, folio, cliente, proyecto } o null
  // Cabecera cliente-facing para el PDF (folio/cliente/vendedor/fechas). Se llena al
  // cargar en modo EDITAR y al guardar (folio sale del backend). null = aun sin guardar.
  const [cabecera, setCabecera] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [toast, setToast] = useState(null);              // aviso flotante temporal
  // Id local: arranca del prop (modo editar) y, al crear en modo nueva, se
  // setea al id devuelto por /generar para que los siguientes "Guardar" hagan PUT.
  const [cotIdLocal, setCotIdLocal] = useState(cotizacionId || null);
  // CARTA-BANCARIA v2: carta de datos bancarios ligada a ESTA cotizacion (hereda su vigencia).
  const [cartaAbierta, setCartaAbierta] = useState(false);
  const modoEditar = cotIdLocal != null;
  // COTIZACIONES-HISTORIAL: modal de versiones.
  const [histAbierto, setHistAbierto] = useState(false);
  // CO-ESTADOS: estado GUARDADO (uno de los 4) y estado EFECTIVO (incluye 'vencida'
  // derivado en lectura). El selector usa el guardado; el chip muestra el efectivo.
  const [estadoCot, setEstadoCot] = useState('borrador');
  const [estadoEfectivo, setEstadoEfectivo] = useState('borrador');
  const [cambiandoEstado, setCambiandoEstado] = useState(false);
  const [enviandoAlmacen, setEnviandoAlmacen] = useState(false);
  // SPRINT DINERO 1: una cotizacion AUTORIZADA es de solo lectura (el backend
  // rechaza la edicion con 409; esto evita intentos fallidos y lo hace visible).
  // Para des-autorizar se pide motivo (modal) y el backend exige rol direccion/gerente.
  const esAutorizada = estadoEfectivo === 'autorizada';

  const [motivoModal, setMotivoModal] = useState(null); // null | { destino }
  const [motivoTexto, setMotivoTexto] = useState('');
  // EXPORTAR-A-COLECCION: modal de "Crear catalogo / Crear lista congelada".
  // modalCol = null | { congelar:boolean }. Aditivo: no toca el flujo de la cotizacion.
  const [modalCol, setModalCol] = useState(null);
  const [colNombre, setColNombre] = useState('');
  const [colIcono, setColIcono] = useState('layers');
  const [colColor, setColColor] = useState('#5c9adb');
  const [colDias, setColDias] = useState(30); // dias de vigencia para lista congelada
  const [creandoCol, setCreandoCol] = useState(false);

  // Sincroniza el id local si cambia el prop (navegar a editar otra cotizacion).
  useEffect(() => { setCotIdLocal(cotizacionId || null); }, [cotizacionId]);

  // Aviso flotante con TIPO: 'ok' (sutil, verde, abajo) | 'warn' (ambar, centrado
  // arriba, grande) | 'error' (rojo, igual de prominente que warn). Auto-cierre por
  // tipo: ok ~2.5s; warn/error ~4.5s. Un ref evita que timers se solapen.
  const toastTimer = useRef(null);
  const mostrarToast = useCallback((msg, tipo = 'ok') => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ msg, tipo });
    const dur = tipo === 'ok' ? 2500 : 4500;
    toastTimer.current = setTimeout(() => setToast(null), dur);
  }, []);

  // TERMINOS-COTIZACION: catalogo de clausulas (una vez) y preseleccion de una cotizacion NUEVA.
  useEffect(() => {
    fetch(`${API_BASE_CLI}/api/clausulas-cotizacion`, { headers: authHeaders() })
      .then(r => (r.ok ? r.json() : null))
      .then(j => { if (j && j.ok) { setCatClausulas(j.clausulas || []); setFamiliasCat(j.familias || []); } })
      .catch(() => {});
  }, []);
  // "Lleva servicio" = hay trabajo en sitio. Una venta de material suelto (cable incluido) solo
  // lleva las fijas: las seleccionables hablan del sitio, la red y la puesta en marcha.
  const conServicioCot = useMemo(() => lineas.some(l => l.tipo_catalogo === 'servicio'), [lineas]);
  const textoCot = useMemo(() => [nombre, ...lineas.map(l => l.nombre)].filter(Boolean).join(' · ').slice(0, 1500), [nombre, lineas]);
  const pedirSugeridas = useCallback(async () => {
    const qs = new URLSearchParams({ con_servicio: conServicioCot ? '1' : '0', texto: textoCot });
    if (expediente?.id) qs.set('oportunidad_id', String(expediente.id));
    const r = await fetch(`${API_BASE_CLI}/api/clausulas-cotizacion/sugeridas?${qs}`, { headers: authHeaders() });
    const j = r.ok ? await r.json() : null;
    return j && j.ok ? j : null;
  }, [conServicioCot, textoCot, expediente]);
  useEffect(() => {
    // Solo cotizacion NUEVA y mientras nadie haya tocado las casillas: lo que se marque a mano manda.
    if (modoEditar || terminosTocados || cargando) return;
    let vivo = true;
    pedirSugeridas().then(j => {
      if (!vivo || !j) return;
      setSelTerminos(j.ids || []);
      setFamiliasSugeridas(j.familias || []);
    }).catch(() => {});
    return () => { vivo = false; };
  }, [modoEditar, terminosTocados, cargando, pedirSugeridas]);
  // Cotizacion anterior a los terminos: se añaden SOLO si alguien pulsa el boton.
  const activarTerminos = useCallback(async () => {
    setCargandoTerminos(true);
    try {
      const j = await pedirSugeridas();
      if (j) { setSelTerminos(j.ids || []); setFamiliasSugeridas(j.familias || []); setTerminosTocados(true); }
      else mostrarToast('No se pudieron cargar los términos y condiciones.', 'error');
    } finally { setCargandoTerminos(false); }
  }, [pedirSugeridas, mostrarToast]);

  // Formatea un monto en MXN (como esta guardado) hacia la moneda de presentacion.
  // USD convierte con el TC de venta. SIN-REDONDEO (2026-10-12): 2 decimales en las DOS monedas
  // (antes MXN iba sin centavos: un total de $12,345.67 se leia $12,346).
  const fmt = useCallback((mxn) => {
    const n = Number(mxn) || 0;
    const v = monedaCot === 'USD' ? convertir(n, 'MXN', 'USD') : n;
    const dec = 2;
    return '$' + v.toLocaleString('es-MX', { minimumFractionDigits: dec, maximumFractionDigits: dec }) + ' ' + monedaCot;
  }, [monedaCot, convertir]);

  // Margen en PESOS por linea (INTERNO, no visible al cliente): formato "+$1,234",
  // verde, respetando la moneda de presentacion (MXN/USD). El monto entra en MXN.
  const fmtMargenLinea = useCallback((mxn) => {
    const n = Number(mxn) || 0;
    const v = monedaCot === 'USD' ? convertir(n, 'MXN', 'USD') : n;
    const dec = 2;
    return '+$' + v.toLocaleString('es-MX', { minimumFractionDigits: dec, maximumFractionDigits: dec });
  }, [monedaCot, convertir]);

  // Carga inicial. Tres modos segun los props:
  //  - EDITAR  (solo cotizacionId): cabecera + lineas desde la cotizacion.
  //  - NUEVA   (solo carritoId): lineas desde el carrito.
  //  - REGRESO (cotizacionId Y carritoId): cabecera desde la cotizacion + lineas
  //    desde el CARRITO (viejo + lo recien agregado en La Tienda).
  useEffect(() => {
    let activo = true;
    setCargando(true);
    setError(null);

    // Mapea un carrito_item a linea editable.
    const lineaDeItem = (it) => {
      const costo = Number(it.costo_unitario_mxn) || 0;
      const precio = Number(it.precio_unitario_mxn) || 0;
      let margen = null;
      if (costo > 0) {
        margen = (it.margen_pct != null && it.margen_pct !== '')
          ? Number(it.margen_pct)
          : margenDePrecioCosto(precio, costo);
      }
      return {
        key: it.id,
        nombre: it.nombre,
        sku: it.referencia_id || null,
        // EXPORTAR-A-COLECCION: tipo FUENTE (syscom_producto/beep_producto/...) que
        // necesita coleccion_items. Viene del carrito (it.tipo). En modo editar (desde
        // cotizacion guardada) no se persiste -> queda null y la linea no es exportable.
        fuente: it.tipo || null,
        // FICHA-PRODUCTO: id_unico derivado igual que el backend (mismo formato que tienda.js)
        // para que la linea traida del carrito tambien abra su ficha. Solo catalogo con ficha.
        id_unico: (() => {
          const ref = String(it.referencia_id || '');
          const base = ref.includes(':') ? ref.split(':')[0] : ref;
          if (!base) return null;
          if (it.tipo === 'syscom_producto') return `syscom_${base}`;
          if (it.tipo === 'beep_producto') { const n = Number(base); return Number.isFinite(n) ? `beep_prod_${n}` : null; }
          return null;
        })(),
        marca: it.marca || null,
        modelo: it.modelo || null,
        tipo_catalogo: it.tipo_catalogo || 'producto',
        imagen_url: it.imagen_url || null,
        cantidad: Math.max(1, Math.round(Number(it.cantidad) || 1)),
        costo_unitario: costo,
        precio_unitario: precio,
        precio_lista_mxn: it.precio_lista_mxn != null ? Number(it.precio_lista_mxn) : null,
        descuento_pct: 0,
        margen_pct: margen,
      };
    };
    // Mapea un cotizacion_producto a linea editable.
    const lineaDeProd = (p, i) => {
      const costo = Number(p.costo_unitario) || 0;
      const precio = Number(p.precio_unitario) || 0;
      return {
        key: `p${i}`,
        id: p.id != null ? p.id : null, // id de BD de la linea (para sustituir)
        nombre: p.nombre,
        sku: p.sku || null,
        // Desde cotizacion guardada la fuente no se persiste (solo la categoria). Si el
        // backend algun dia la expone (p.fuente), se usa; si no, la linea no es exportable.
        fuente: p.fuente || null,
        // FICHA-PRODUCTO: id_unico que manda el backend por linea (syscom_/beep_prod_) para
        // abrir la ficha de detalle desde la cotizacion guardada. Sin esto el producto no
        // seria clickeable al reabrir una cotizacion (flujo mas comun). Solo lectura.
        id_unico: p.id_unico || null,
        // GARANTIAS: id de la linea guardada (para editar su garantia por cotizacion).
        lineaId: p.id != null ? p.id : null,
        marca: p.marca || null,
        modelo: p.modelo || null,
        // PDF-DESCRIPCION: la copia de la linea (o la del producto si es vieja); viaja de vuelta al guardar.
        descripcion: p.descripcion || null,
        tipo_catalogo: p.tipo || 'producto',
        imagen_url: p.imagen_url || null,
        cantidad: Math.max(1, Math.round(Number(p.cantidad) || 1)),
        costo_unitario: costo,
        precio_unitario: precio,
        precio_lista_mxn: p.precio_lista_mxn != null ? Number(p.precio_lista_mxn) : null,
        descuento_pct: 0,
        margen_pct: costo > 0 ? margenDePrecioCosto(precio, costo) : null,
      };
    };

    async function cargar() {
      try {
        if (cotizacionId != null) {
          // EDITAR o REGRESO: la cabecera SIEMPRE sale de la cotizacion.
          // SEGURIDAD-ENDPOINTS-ABIERTOS (2026-09-18): este GET iba SIN token. No porque hiciera
          // falta (este editor solo se abre con sesion iniciada), sino por descuido — el PUT de
          // mas abajo si lo mandaba. Ese descuido era lo unico que mantenia abierta la puerta por
          // la que se leia el costo y el margen de cualquier cotizacion desde fuera.
          const r = await fetch(`${API_BASE_CLI}/api/cotizaciones/${cotizacionId}`, { headers: authHeaders() }).then(x => x.json());
          if (!activo) return;
          if (!r || !r.cotizacion) { setError((r && r.error) || 'No se pudo cargar la cotizacion'); return; }
          const cot = r.cotizacion || {};
          setNombre(cot.nombre || '');
          setNota(cot.notas_cliente || '');
          setMonedaCot(cot.moneda === 'USD' ? 'USD' : 'MXN');
          // Condiciones del documento (paginas 2 y 3)
          const ant = Number.isFinite(Number(cot.anticipo_pct)) ? Number(cot.anticipo_pct) : 100;
          setAnticipoPct([100, 80, 70, 50].includes(ant) ? ant : -1);
          setAnticipoOtro([100, 80, 70, 50].includes(ant) ? '' : String(ant));
          const sd = Number(cot.saldo_dias);
          setSaldoModo(!sd ? null : ([15, 30].includes(sd) ? sd : -1));
          setSaldoOtro(sd && ![15, 30].includes(sd) ? String(sd) : '');
          setTiempoEntrega(cot.tiempo_entrega || '');
          setIncluirPago(cot.incluir_pagina_pago !== false);
          setIncluirCert(cot.incluir_certificado !== false);
          setMostrarDescripcion(cot.pdf_mostrar_descripcion_efectivo === true);
          setDescripcionDefinida(true);
          setCuentasSel(Array.isArray(cot.cuentas_bancarias_ids) ? cot.cuentas_bancarias_ids : null);
          // TERMINOS-COTIZACION: la foto guardada manda; null = cotizacion anterior.
          setFotoTerminos(Array.isArray(cot.terminos) ? cot.terminos : null);
          setSelTerminos(Array.isArray(cot.terminos) ? cot.terminos.map(t => Number(t.clausula_id)) : null);
          setDuplicadaDeFolio(cot.duplicada_de_folio || null);
          setVigenciaDias(Number(cot.vigencia_dias) || 15);
          const gl = {};
          for (const p of (r.productos || [])) {
            if (p.id != null) gl[p.id] = { garantia_meses: p.garantia_meses ?? null, garantia_dias: p.garantia_dias ?? null, garantia_origen: p.garantia_origen || null, editada: !!p.garantia_editada };
          }
          setGarantiasLinea(gl);
          setEstadoCot(cot.estado || 'borrador');
          setEstadoEfectivo(cot.estado_efectivo || cot.estado || 'borrador');
          setCabecera({
            folio: cot.folio || null,
            cliente_grupo: cot.cliente_grupo || null,
            sucursal: cot.sucursal || null,
            vendedor_nombre: cot.vendedor_nombre || null,
            creado_at: cot.creado_at || null,
            vence_at: cot.vence_at || null,
          });
          if (cot.oportunidad_id) {
            setExpediente({
              id: cot.oportunidad_id,
              folio: cot.oportunidad_folio || '',
              // OJO: aqui va el cliente REAL o null. NUNCA una etiqueta de relleno: este campo
              // terminaba imprimiendose como nombre del cliente en el PDF que ve el cliente
              // (defecto reportado por Ricardo en OP-2026-0338, que no tiene cliente ligado).
              cliente: cot.cliente_grupo || cot.sucursal || null,
              proyecto: '',
            });
          } else {
            setExpediente(null);
          }
          if (carritoId != null) {
            // REGRESO: las lineas (viejo + nuevo) vienen del carrito.
            const rc = await fetch(`${API_BASE_CLI}/api/tienda/carritos/${carritoId}`, { headers: authHeaders() }).then(x => x.json());
            if (!activo) return;
            if (rc && rc.ok) setLineas(ordenarLineas((rc.items || []).map(lineaDeItem)));
            else setError((rc && rc.error) || 'No se pudo cargar el carrito');
          } else {
            // EDITAR: las lineas vienen de la cotizacion.
            setLineas(ordenarLineas((r.productos || []).map(lineaDeProd)));
          }
          return;
        }

        // NUEVA: desde el carrito.
        if (!carritoId) { setError('Carrito no especificado'); return; }
        const rc = await fetch(`${API_BASE_CLI}/api/tienda/carritos/${carritoId}`, { headers: authHeaders() }).then(x => x.json());
        if (!activo) return;
        if (rc && rc.ok) setLineas(ordenarLineas((rc.items || []).map(lineaDeItem)));
        else setError((rc && rc.error) || 'No se pudo cargar el carrito');
      } catch (e) {
        if (activo) setError('Sin conexion');
      } finally {
        if (activo) setCargando(false);
      }
    }
    cargar();
    return () => { activo = false; };
  }, [carritoId, cotizacionId]);

  const setLinea = useCallback((key, campo, valor) => {
    setLineas(arr => arr.map(l => l.key === key ? { ...l, [campo]: valor } : l));
  }, []);
  const quitarLinea = useCallback((key) => {
    setLineas(arr => arr.filter(l => l.key !== key));
  }, []);

  // DnD #4a: sensores mobile-safe (touch con delay 200) y reordenamiento por arrastre. Solo
  // cambia el ORDEN del array (presentacion); los totales son sumas -> NO cambian. Se persiste
  // con el "Guardar cotizacion" actual (insertarProductos escribe orden por posicion).
  const sensoresDnD = useSensoresDnD();
  const onDragEndLinea = useCallback((event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setLineas(arr => {
      const oldIndex = arr.findIndex(l => l.key === active.id);
      const newIndex = arr.findIndex(l => l.key === over.id);
      if (oldIndex < 0 || newIndex < 0) return arr;
      return arrayMove(arr, oldIndex, newIndex);
    });
  }, []);

  // Perilla de margen POR RENGLON: margen <-> precio enlazados.
  // Al cambiar Margen %: precio = round2(costo / (1 - m/100)), m = clamp(0, 95).
  const onMargenChange = useCallback((key, valor) => {
    setLineas(arr => arr.map(l => {
      if (l.key !== key) return l;
      const costoU = Number(l.costo_unitario) || 0;
      if (costoU <= 0) return { ...l, margen_pct: valor }; // sin costo: no se deriva precio
      const m = Math.min(95, Math.max(0, Number(valor) || 0));
      const precio = round2(costoU / (1 - m / 100));
      return { ...l, margen_pct: valor, precio_unitario: precio };
    }));
  }, []);

  // Al cambiar Precio venta: margen = (precio - costo)/precio * 100 (si precio>0 y costo>0).
  const onPrecioChange = useCallback((key, valor) => {
    setLineas(arr => arr.map(l => {
      if (l.key !== key) return l;
      const precio = Number(valor) || 0;
      const costoU = Number(l.costo_unitario) || 0;
      const margen = (precio > 0 && costoU > 0) ? round2(((precio - costoU) / precio) * 100) : l.margen_pct;
      return { ...l, precio_unitario: valor, margen_pct: margen };
    }));
  }, []);

  // Calculo por linea + agregados por tipo_catalogo.
  // Sin descuento manual: el precio_unitario YA es el precio final (ajustado por la perilla de margen).
  const { calc, costoProd, costoInst, subtotal, iva, total } = useMemo(() => {
    const calc = lineas.map(l => {
      const cant = Math.max(1, Math.round(Number(l.cantidad) || 1));
      const precioBase = Number(l.precio_unitario) || 0;
      const sub = precioBase * cant;
      const costo = (Number(l.costo_unitario) || 0) * cant;
      return { ...l, cant, precioFinal: precioBase, sub, costo };
    });
    let costoProd = 0, costoInst = 0, subtotal = 0;
    for (const l of calc) {
      subtotal += l.sub;
      if (esInstalacion(l.tipo_catalogo)) costoInst += l.costo;
      else costoProd += l.costo;
    }
    const iva = subtotal * TASA_IVA;
    return { calc, costoProd, costoInst, subtotal, iva, total: subtotal + iva };
  }, [lineas]);

  const costoTotal = costoProd + costoInst;
  const utilidadNeta = subtotal - costoTotal;
  const margenNeto = subtotal > 0 ? (utilidadNeta / subtotal) * 100 : 0;

  // Payload de productos desde las lineas actuales (el precio_unitario ya viene
  // ajustado por la perilla de margen, por eso descuento_pct = 0).
  // Anticipo y saldo EFECTIVOS (lo que se manda y se imprime). 0% no existe en Beep (Ricardo
  // 2026-10-12): se bloquea el guardado con un mensaje; el servidor tambien lo rechaza (400).
  const anticipoEfectivo = anticipoPct === -1 ? Math.min(100, Math.max(0, Math.trunc(Number(anticipoOtro) || 0))) : anticipoPct;
  const saldoDiasEfectivo = anticipoEfectivo >= 100 ? null
    : (saldoModo === -1 ? (Math.trunc(Number(saldoOtro)) || null) : saldoModo);
  const errorCondiciones = anticipoEfectivo < 1
    ? 'El anticipo no puede ser 0%. Indica el porcentaje que se cobra por adelantado.'
    : (anticipoEfectivo < 100 && saldoModo === -1 && !(saldoDiasEfectivo >= 1 && saldoDiasEfectivo <= 365))
      ? 'Indica en cuántos días se paga el saldo (de 1 a 365).' : null;

  const construirProductos = useCallback(() => lineas.map(l => ({
    nombre: l.nombre,
    sku: l.sku,
    imagen_url: l.imagen_url || null,
    cantidad: Math.max(1, Math.round(Number(l.cantidad) || 1)),
    precio_unitario: Number(l.precio_unitario) || 0,
    costo_unitario: Number(l.costo_unitario) || 0,
    descuento_pct: 0,
    tipo: l.tipo_catalogo,
    fuente: l.fuente || null, // EXPORTAR-A-COLECCION: persiste el origen por linea
    descripcion: l.descripcion || null, // PDF-DESCRIPCION: la copia ya guardada se conserva
    categoria: null,
    // GARANTIAS: solo viaja si el vendedor la edito en esta cotizacion (si no, el backend
    // resuelve el default del catalogo/empresa al vuelo).
    garantia_meses: l.lineaId != null && garantiasLinea[l.lineaId] && garantiasLinea[l.lineaId].editada ? garantiasLinea[l.lineaId].garantia_meses : null,
    garantia_dias: l.lineaId != null && garantiasLinea[l.lineaId] && garantiasLinea[l.lineaId].editada ? garantiasLinea[l.lineaId].garantia_dias : null,
    garantia_origen: l.lineaId != null && garantiasLinea[l.lineaId] && garantiasLinea[l.lineaId].editada ? garantiasLinea[l.lineaId].garantia_origen : null,
  })), [lineas, garantiasLinea]);

  // Guarda la cotizacion (PUT si editar, POST /generar si nueva) y devuelve el id
  // firme. Actualiza cotIdLocal/cabecera. No muestra toasts (lo hace quien llama).
  // Devuelve { ok:true, id, folio, status } o { ok:false, status, error }.
  const persistir = useCallback(async () => {
    if (errorCondiciones) return { ok: false, status: 400, error: errorCondiciones };
    const productos = construirProductos();
    const url = modoEditar
      ? `${API_BASE_CLI}/api/cotizaciones/${cotIdLocal}`
      : `${API_BASE_CLI}/api/cotizaciones/generar`;
    const resp = await fetch(url, {
      method: modoEditar ? 'PUT' : 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        carrito_id: carritoId,
        creado_por: usuario?.id || null,
        oportunidad_id: expediente?.id || null,
        nombre: nombre.trim() || null,
        nota: nota.trim() || null,
        moneda: monedaCot,
        productos,
        // Condiciones del documento (informativas)
        anticipo_pct: anticipoEfectivo,
        saldo_dias: saldoDiasEfectivo,
        tiempo_entrega: tiempoEntrega.trim() || null,
        incluir_pagina_pago: incluirPago,
        incluir_certificado: incluirCert,
        pdf_mostrar_descripcion: descripcionDefinida ? mostrarDescripcion : null,
        cuentas_bancarias_ids: cuentasSel,
        // TERMINOS-COTIZACION: solo si hay seleccion (null = cotizacion anterior sin terminos:
        // no se manda y el servidor no toca nada). Las fijas las pone el servidor siempre.
        terminos_ids: selTerminos === null ? undefined : selTerminos,
      }),
    });
    const r = await resp.json().catch(() => ({}));
    if (resp.ok && (r.ok || r.folio)) {
      const idFirme = modoEditar ? cotIdLocal : (r.cotizacion_id || r.id || null);
      if (!modoEditar && idFirme != null) setCotIdLocal(idFirme);
      if (r.folio) setCabecera(prev => ({ ...(prev || {}), folio: r.folio }));
      // PDF-DESCRIPCION: en una cotizacion nueva el servidor pudo heredar la eleccion del cliente: se adopta.
      if (typeof r.pdf_mostrar_descripcion === 'boolean' && !descripcionDefinida) { setMostrarDescripcion(r.pdf_mostrar_descripcion); setDescripcionDefinida(true); }
      // La foto que guardo el servidor es la que vale (texto incluido): se adopta tal cual.
      if (Array.isArray(r.terminos)) {
        setFotoTerminos(r.terminos);
        setSelTerminos(r.terminos.map(t => Number(t.clausula_id)));
      }
      return { ok: true, id: idFirme, folio: r.folio || null, status: resp.status, terminos: Array.isArray(r.terminos) ? r.terminos : null,
        pdfMostrarDescripcion: descripcionDefinida ? mostrarDescripcion : (typeof r.pdf_mostrar_descripcion === 'boolean' ? r.pdf_mostrar_descripcion : mostrarDescripcion) };
    }
    return { ok: false, status: resp.status, error: r.error || null };
  }, [construirProductos, modoEditar, cotIdLocal, carritoId, usuario, expediente, nombre, nota, monedaCot, anticipoEfectivo, saldoDiasEfectivo, errorCondiciones, tiempoEntrega, incluirPago, incluirCert, descripcionDefinida, mostrarDescripcion, cuentasSel, selTerminos]);

  // CONDICIONES DEL DOCUMENTO en cotizacion AUTORIZADA (Ricardo 2026-09-07): anticipo, tiempo de
  // entrega, cuentas, switches y garantia por linea son presentacion, no dinero. Se guardan con
  // PATCH /condiciones + PATCH garantia por linea, sin pasar por el PUT (que el candado rechaza).
  const [guardandoCond, setGuardandoCond] = useState(false);
  const guardarCondiciones = useCallback(async () => {
    if (!cotIdLocal) return { ok: false, error: 'Guarda la cotizacion primero' };
    if (errorCondiciones) return { ok: false, error: errorCondiciones };
    setGuardandoCond(true);
    try {
      const rc = await fetch(`${API_BASE_CLI}/api/cotizaciones/${cotIdLocal}/condiciones`, {
        method: 'PATCH', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          anticipo_pct: anticipoEfectivo,
          saldo_dias: saldoDiasEfectivo,
          tiempo_entrega: tiempoEntrega.trim() || null,
          incluir_pagina_pago: incluirPago,
          incluir_certificado: incluirCert,
          pdf_mostrar_descripcion: descripcionDefinida ? mostrarDescripcion : null,
          cuentas_bancarias_ids: cuentasSel,
        }),
      });
      const jc = await rc.json().catch(() => ({}));
      if (!rc.ok || !jc.ok) return { ok: false, error: jc.error || `No se pudieron guardar las condiciones (${rc.status})` };
      // TERMINOS-COTIZACION: el servidor re-escribe "Forma de pago" con el anticipo nuevo.
      if (Array.isArray(jc.terminos)) setFotoTerminos(jc.terminos);
      // Garantia por linea: solo las editadas aqui (el backend rechaza 409 si la garantia ya inicio).
      const errores = [];
      for (const l of lineas) {
        const g = l.lineaId != null ? garantiasLinea[l.lineaId] : null;
        if (!g || !g.editada) continue;
        const rg = await fetch(`${API_BASE_CLI}/api/garantias/cotizacion/${cotIdLocal}/linea/${l.lineaId}`, {
          method: 'PATCH', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ garantia_meses: g.garantia_meses, garantia_dias: g.garantia_dias, garantia_origen: g.garantia_origen }),
        });
        const jg = await rg.json().catch(() => ({}));
        if (!rg.ok || !jg.ok) errores.push(jg.error || `linea ${l.lineaId}: ${rg.status}`);
      }
      // Las condiciones YA quedaron guardadas; solo fallo alguna garantia por linea (p. ej. 409: ya inicio).
      if (errores.length) return { ok: false, condicionesGuardadas: true, error: errores[0], terminos: Array.isArray(jc.terminos) ? jc.terminos : null };
      return { ok: true, terminos: Array.isArray(jc.terminos) ? jc.terminos : null };
    } catch (e) {
      return { ok: false, error: e.message || 'Error de red' };
    } finally { setGuardandoCond(false); }
  }, [cotIdLocal, anticipoEfectivo, saldoDiasEfectivo, errorCondiciones, tiempoEntrega, incluirPago, incluirCert, descripcionDefinida, mostrarDescripcion, cuentasSel, lineas, garantiasLinea]);

  const guardar = useCallback(async () => {
    if (lineas.length === 0) return;
    setGuardando(true);
    try {
      const res = await persistir();
      if (res.ok) {
        mostrarToast(`Cotizacion ${res.folio || ''} guardada`.trim(), 'ok');
      } else if (res.status === 409) {
        // Nombre duplicado en el ambito: aviso claro (ambar), SIN perder el trabajo.
        mostrarToast(res.error || 'Ya existe una cotizacion con ese nombre. Cambia el nombre e intenta de nuevo.', 'warn');
      } else {
        throw new Error(res.error || 'No se pudo guardar');
      }
    } catch (e) {
      mostrarToast('No se pudo guardar la cotizacion. ' + (e.message || ''), 'error');
    } finally {
      setGuardando(false);
    }
  }, [lineas.length, persistir, mostrarToast]);

  // ROUND-TRIP "Agregar producto": guarda la cotizacion sola, pobla el carrito
  // ACTIVO con sus lineas (POST /:id/a-carrito) y navega a La Tienda en modo
  // "agregando". El carrito se resuelve con el MISMO vendedor + cliente del
  // selector de la Tienda, para que La Tienda use exactamente ese carrito.
  const [yendoTienda, setYendoTienda] = useState(false);
  // MOTOR DE ORIGEN Fase 1: el "+ Agregar producto" abre primero este menu de origen.
  // Solo navegacion: al elegir "La Tienda" se llama agregarEnTienda() (intacta).
  const [origenAbierto, setOrigenAbierto] = useState(false);
  // MOTOR DE ORIGEN Fase 2 (origen Coleccion): { carritoId, cotizacionId } cuando el
  // carrito ya quedo SEMBRADO con las lineas actuales y el navegador de coleccion esta abierto.
  const [colOrigen, setColOrigen] = useState(null);
  const [sembrandoCol, setSembrandoCol] = useState(false);
  const agregarEnTienda = useCallback(async () => {
    if (lineas.length === 0 || yendoTienda || guardando) return;
    setYendoTienda(true);
    try {
      const res = await persistir();
      if (!res.ok) {
        if (res.status === 409) mostrarToast(res.error || 'Ya existe una cotizacion con ese nombre. Cambia el nombre e intenta de nuevo.', 'warn');
        else mostrarToast(res.error || 'No se pudo guardar antes de ir a La Tienda', 'error');
        return;
      }
      const cotId = res.id;
      if (cotId == null) { mostrarToast('No se pudo identificar la cotizacion', 'error'); return; }
      const clienteSel = leerClienteStorage();
      const vendedorId = usuario?.correo || usuario?.email || usuario?.nombre || 'sin-vendedor';
      const rc = await fetch(`${API_BASE_CLI}/api/cotizaciones/${cotId}/a-carrito`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          vendedor_id: vendedorId,
          cliente_id: clienteSel?.id || null,
          cliente_nombre: clienteSel?.nombre || null,
        }),
      }).then(x => x.json());
      if (rc && rc.ok && rc.carrito_id) {
        window.dispatchEvent(new CustomEvent('beep-agregar-en-tienda', { detail: { carritoId: rc.carrito_id, cotizacionId: cotId } }));
      } else {
        throw new Error((rc && rc.error) || 'No se pudo preparar el carrito');
      }
    } catch (e) {
      mostrarToast('No se pudo abrir La Tienda. ' + (e.message || ''), 'error');
    } finally {
      setYendoTienda(false);
    }
  }, [lineas.length, yendoTienda, guardando, persistir, usuario, mostrarToast]);

  // MOTOR DE ORIGEN Fase 2: "Una coleccion". Mismo arranque que agregarEnTienda (guardar +
  // SEMBRAR el carrito con las lineas actuales via /a-carrito), pero en vez de navegar a La
  // Tienda abre el navegador de coleccion. El carrito se siembra UNA sola vez aqui; el modal
  // solo AGREGA encima (no re-siembra) para no perder lo que se vaya añadiendo.
  const irAColeccionOrigen = useCallback(async () => {
    if (lineas.length === 0 || yendoTienda || guardando || sembrandoCol) return;
    setSembrandoCol(true);
    try {
      const res = await persistir();
      if (!res.ok) {
        if (res.status === 409) mostrarToast(res.error || 'Ya existe una cotizacion con ese nombre. Cambia el nombre e intenta de nuevo.', 'warn');
        else mostrarToast(res.error || 'No se pudo guardar antes de abrir la coleccion', 'error');
        return;
      }
      const cotId = res.id;
      if (cotId == null) { mostrarToast('No se pudo identificar la cotizacion', 'error'); return; }
      const clienteSel = leerClienteStorage();
      const vendedorId = usuario?.correo || usuario?.email || usuario?.nombre || 'sin-vendedor';
      const rc = await fetch(`${API_BASE_CLI}/api/cotizaciones/${cotId}/a-carrito`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          vendedor_id: vendedorId,
          cliente_id: clienteSel?.id || null,
          cliente_nombre: clienteSel?.nombre || null,
        }),
      }).then(x => x.json());
      if (rc && rc.ok && rc.carrito_id) {
        setColOrigen({ carritoId: rc.carrito_id, cotizacionId: cotId });
      } else {
        throw new Error((rc && rc.error) || 'No se pudo preparar el carrito');
      }
    } catch (e) {
      mostrarToast('No se pudo abrir la coleccion. ' + (e.message || ''), 'error');
    } finally {
      setSembrandoCol(false);
    }
  }, [lineas.length, yendoTienda, guardando, sembrandoCol, persistir, usuario, mostrarToast]);

  // MOTOR DE ORIGEN F3.B: en EDITAR, entrar al "modo agregar a cotizacion" (independiente
  // del carrito). Guarda la cotizacion (para no perder ediciones al volver), activa el modo
  // global y navega al tab elegido (La Tienda / Colecciones / Listas). F3.B NO agrega
  // productos todavia: solo lleva a la pagina, muestra la barra y oculta el carrito.
  const entrarModoAgregar = useCallback(async (tab, sustituirIdxArg = null) => {
    if (yendoTienda || guardando || sembrandoCol) return;
    if (lineas.length === 0) { mostrarToast('Agrega al menos un producto primero', 'warn'); return; }
    setSembrandoCol(true);
    try {
      const res = await persistir();
      if (!res.ok) {
        if (res.status === 409) mostrarToast(res.error || 'Ya existe una cotizacion con ese nombre. Cambia el nombre e intenta de nuevo.', 'warn');
        else mostrarToast(res.error || 'No se pudo guardar antes de agregar productos', 'error');
        return;
      }
      const cotId = res.id;
      if (cotId == null) { mostrarToast('No se pudo identificar la cotizacion', 'error'); return; }
      const nombreCot = (nombre && nombre.trim()) || cabecera?.folio || res.folio || 'la cotizacion';
      // Mejora #4b: si es SUSTITUIR, re-derivar el id FRESCO de la linea por su POSICION (el PUT
      // de guardado reasigna ids; el orden es estable -> la linea en esa posicion es la misma).
      let sustituirLineaId = null;
      if (sustituirIdxArg != null) {
        try {
          const rc = await fetch(`${API_BASE_CLI}/api/cotizaciones/${cotId}`, { headers: authHeaders() }).then(x => x.json());
          const prods = (rc && rc.productos) || [];
          sustituirLineaId = prods[sustituirIdxArg] ? prods[sustituirIdxArg].id : null;
        } catch { /* cae al aviso de abajo */ }
        if (sustituirLineaId == null) { mostrarToast('No se pudo identificar la linea a sustituir', 'error'); return; }
      }
      activarModoAgregar({ id: cotId, nombre: nombreCot, sustituirLineaId });
      window.dispatchEvent(new CustomEvent('beep-ir-tab', { detail: { tab } }));
    } catch (e) {
      mostrarToast('No se pudo abrir el modo agregar. ' + (e.message || ''), 'error');
    } finally {
      setSembrandoCol(false);
    }
  }, [yendoTienda, guardando, sembrandoCol, lineas.length, persistir, nombre, cabecera, mostrarToast]);

  // Mejora #4b: "Sustituir producto" — abre el MISMO menu de origen, pero apuntando a la linea
  // (por su POSICION; el id de BD se re-deriva tras guardar). Tras elegir, el origen llama al
  // PUT .../sustituir en vez del POST append, y se vuelve al cotizador.
  const [sustituirIdx, setSustituirIdx] = useState(null);
  const pedirSustituir = useCallback((key) => {
    if (yendoTienda || guardando || sembrandoCol) return;
    const idx = lineas.findIndex(x => x.key === key);
    if (idx < 0) return;
    setSustituirIdx(idx);
    setOrigenAbierto(true);
  }, [lineas, yendoTienda, guardando, sembrandoCol]);

  // ROUND-TRIP "Vincular/cambiar expediente": guarda la cotizacion sola y navega a
  // la PAGINA COMPLETA de Expedientes en modo seleccion. Al elegir (o cancelar) se
  // reabre el editor recargando la cotizacion ya vinculada.
  const [yendoExpediente, setYendoExpediente] = useState(false);
  const irAElegirExpediente = useCallback(async () => {
    if (yendoExpediente || yendoTienda || guardando) return;
    if (lineas.length === 0) {
      mostrarToast('Agrega al menos un producto antes de vincular un expediente', 'warn');
      return;
    }
    setYendoExpediente(true);
    try {
      const res = await persistir();
      if (!res.ok) {
        if (res.status === 409) mostrarToast(res.error || 'Ya existe una cotizacion con ese nombre. Cambia el nombre e intenta de nuevo.', 'warn');
        else mostrarToast(res.error || 'No se pudo guardar antes de vincular', 'error');
        return;
      }
      const cotId = res.id;
      if (cotId == null) { mostrarToast('No se pudo identificar la cotizacion', 'error'); return; }
      window.dispatchEvent(new CustomEvent('beep-elegir-expediente', {
        detail: { cotizacionId: cotId, folio: res.folio || cabecera?.folio || '' },
      }));
    } catch (e) {
      mostrarToast('No se pudo abrir Expedientes. ' + (e.message || ''), 'error');
    } finally {
      setYendoExpediente(false);
    }
  }, [yendoExpediente, yendoTienda, guardando, lineas.length, persistir, cabecera, mostrarToast]);

  // Descargar PDF cliente-facing. SOLO nombre/cantidad/precio_unitario/subtotal por linea
  // y totales en pantalla. Sin costo, margen, desglose interno ni "SYSCOM".
  const descargarPDF = useCallback(async () => {
    const folio = cabecera?.folio;
    if (!cotIdLocal || !folio) {
      mostrarToast('Guarda la cotizacion antes de generar el PDF', 'warn');
      return;
    }
    setGenerandoPDF(true);
    try {
      // 1) Condiciones al dia (si es editable): asi el documento y el sistema dicen lo mismo.
      // TERMINOS-COTIZACION: el PDF imprime la foto que devuelve el servidor al guardar, nunca
      // lo que hay en pantalla: asi el documento dice exactamente lo que quedo guardado.
      let terminosPDF = fotoTerminos;
      let conDescripcion = mostrarDescripcion;
      if (!esAutorizada) {
        const rp = await persistir();
        if (rp && rp.ok && Array.isArray(rp.terminos)) terminosPDF = rp.terminos;
        if (rp && rp.ok && typeof rp.pdfMostrarDescripcion === 'boolean') conDescripcion = rp.pdfMostrarDescripcion;
      }
      else {
        const rc = await guardarCondiciones();
        if (rc && Array.isArray(rc.terminos)) terminosPDF = rc.terminos;
        if (!rc.ok) mostrarToast((rc.condicionesGuardadas ? 'Garantía por línea sin cambio: ' : 'Condiciones del documento: ') + (rc.error || 'no se guardaron'), 'warn');
      }
      // 2) Datos del emisor, cuentas y textos de garantia (GET /api/empresa, ya cargado o al vuelo).
      let emp = empresa;
      if (!emp) {
        try { const r = await fetch(`${API_BASE_CLI}/api/empresa`, { headers: authHeaders() }); if (r.ok) emp = await r.json(); } catch { /* fallback */ }
      }
      const e = (emp && emp.empresa) || {};
      const emisor = {
        razonSocial: e.razon_social || EMISOR.razonSocial, rfc: e.rfc || '', direccion: e.direccion_fiscal || '',
        telefono: e.telefono || '', correo: e.correo || '', web: e.web || EMISOR.web,
      };
      const todasCuentas = (emp && emp.cuentas) || [];
      const cuentas = todasCuentas.filter(c => c.mostrar_en_cotizaciones && (cuentasSel ? cuentasSel.includes(c.id) : c.principal));
      // 3) Certificado de garantia (CAPA 1 + CAPA 2): folio GAR y codigo firmados por el servidor.
      let cert = null, qrDataUrl = null, urlVerificacion = null;
      if (incluirCert) {
        const rc = await fetch(`${API_BASE_CLI}/api/garantias/cotizacion/${cotIdLocal}`, { method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' } });
        const jc = await rc.json().catch(() => ({}));
        if (rc.ok && jc.ok) {
          cert = { folio: jc.certificado.folio, codigo: jc.certificado.codigo, fecha_inicio: jc.certificado.fecha_inicio, lineas: jc.lineas };
          // Va DENTRO del PDF y dentro del QR que escanea el cliente: direccion publica, nunca
          // el origen del navegador (desde un build local saldria una liga a localhost).
          urlVerificacion = ligaPublica('garantia', jc.certificado.codigo);
          setCertInfo({ folio: cert.folio, codigo: cert.codigo, url: urlVerificacion });
          // El QR (nivel H + isotipo al centro) se renderiza oculto con qrcode.react y se lee
          // como PNG. Se espera a que el canvas exista y a que el logo ya este pintado.
          let canvas = null;
          for (let i = 0; i < 12 && !canvas; i++) {
            await new Promise(r => setTimeout(r, 90));
            const cv = qrRef.current && qrRef.current.querySelector('canvas');
            if (cv) {
              try {
                const ctx = cv.getContext('2d'); const cx = Math.floor(cv.width / 2), cy = Math.floor(cv.height / 2);
                const px = ctx.getImageData(cx, cy, 1, 1).data; const px2 = ctx.getImageData(cx + 12, cy - 12, 1, 1).data;
                // con el logo pintado, el centro deja de ser blanco/negro puro
                const gris = (p) => Math.abs(p[0] - p[1]) < 8 && Math.abs(p[1] - p[2]) < 8;
                if (!(gris(px) && gris(px2)) || i >= 8) canvas = cv;
              } catch { canvas = cv; }
            }
          }
          if (canvas) { try { qrDataUrl = canvas.toDataURL('image/png'); } catch { qrDataUrl = null; } }
        } else {
          mostrarToast('No se pudo generar el certificado de garantia; el PDF sale sin certificado. ' + (jc.error || ''), 'warn');
        }
      }
      // PDF-DESCRIPCION: la descripcion de cada linea tal como quedo GUARDADA (la copia, o la del producto si la linea
      // es vieja; con `completar` el servidor tambien la pide a Syscom). Se casa por POSICION: el guardado reinserta
      // las lineas en el mismo orden y les cambia el id.
      let descPorPos = null;
      if (conDescripcion) {
        try {
          const rd = await fetch(`${API_BASE_CLI}/api/cotizaciones/${cotIdLocal}?descripciones=completar`, { headers: authHeaders() });
          const jd = await rd.json().catch(() => ({}));
          if (rd.ok && Array.isArray(jd.productos) && jd.productos.length === calc.length) descPorPos = jd.productos.map(p => p.descripcion || null);
        } catch { /* sin descripciones del servidor: sale con las que hay en pantalla */ }
      }
      // Garantia por linea: lo resuelto por el backend (GET) o lo editado aqui.
      const gPorId = garantiasLinea;
      await generarCotizacionPDF({
        emisor,
        cliente: {
          // Solo cliente REAL. El backend ya hereda el del expediente ligado (COALESCE sobre
          // grupo / razon social / persona en el GET), asi que si esto viene vacio es que no hay
          // cliente en ninguna parte y el PDF lo dice con todas sus letras.
          nombre: cabecera?.cliente_grupo || '',
          sucursal: cabecera?.sucursal || '',
          vendedor: cabecera?.vendedor_nombre || '',
          vendedorCorreo: cabecera?.vendedor_correo || '',
        },
        cotizacion: {
          folio,
          fecha: cabecera?.creado_at || null,
          vigencia: cabecera?.vence_at || null,
          nombre: nombre.trim() || null,
          nota: nota.trim() || null,
          moneda: monedaCot,
          expedienteFolio: cabecera?.oportunidad_folio || expediente?.folio || null,
          anticipoPct: anticipoEfectivo,
          saldoDias: saldoDiasEfectivo,
          tiempoEntrega: tiempoEntrega.trim() || null,
          incluirPaginaPago: incluirPago,
          incluirCertificado: incluirCert && !!cert,
          mostrarDescripcion: conDescripcion,
          terminos: Array.isArray(terminosPDF) ? terminosPDF.map(t => ({ titulo: t.titulo, texto: t.texto })) : null,
        },
        lineas: calc.map((l, i) => {
          const g = (l.lineaId != null && gPorId[l.lineaId]) || {};
          const cl = cert && cert.lineas && cert.lineas.find(x => x.cotizacion_producto_id === l.lineaId);
          return {
            nombre: l.nombre,
            marca: l.marca || (cl && cl.marca) || null,
            // FIX-PDF-IMAGENES: el modelo viaja para el fallback digno (si una imagen
            // de verdad no existe, el recuadro muestra el modelo, no un icono roto).
            modelo: l.modelo || (cl && cl.modelo) || null,
            tipo: l.tipo_catalogo || 'producto',
            descripcion: conDescripcion ? ((descPorPos && descPorPos[i]) || l.descripcion || null) : null,
            cantidad: l.cant,
            precio_unitario: Number(l.precio_unitario) || 0,
            subtotal: l.sub,
            imagen_url: l.imagen_url || null,
            garantia_meses: (cl && cl.garantia_meses) ?? g.garantia_meses ?? null,
            garantia_dias: (cl && cl.garantia_dias) ?? g.garantia_dias ?? null,
            garantia_origen: (cl && cl.origen) || g.garantia_origen || null,
          };
        }),
        totales: { subtotal, iva, total },
        cuentas,
        garantiaConfig: {
          que_cubre: e.garantia_que_cubre, apertura_no_cubiertos: e.garantia_apertura_no_cubiertos,
          visita_diagnostico: e.garantia_visita_diagnostico, contacto: e.garantia_contacto,
          terminos_comerciales: e.terminos_comerciales, casos: (emp && emp.casos) || [],
          garantia_instalacion_meses: e.garantia_instalacion_meses, garantia_servicio_dias: e.garantia_servicio_dias,
          correo_comprobantes: e.correo_comprobantes,
        },
        certificado: cert ? { ...cert, lineas: calc.map(l => (cert.lineas || []).find(x => x.cotizacion_producto_id === l.lineaId) || {}) } : null,
        qrDataUrl,
        urlVerificacion,
      });
    } catch (e) {
      window.alert('No se pudo generar el PDF. ' + (e.message || ''));
    } finally {
      setGenerandoPDF(false);
    }
  }, [cabecera, cotIdLocal, expediente, nombre, nota, monedaCot, calc, subtotal, iva, total, mostrarToast, esAutorizada, persistir, guardarCondiciones, empresa, cuentasSel, incluirCert, incluirPago, mostrarDescripcion, anticipoEfectivo, saldoDiasEfectivo, tiempoEntrega, garantiasLinea, fotoTerminos]);

  // COTIZACIONES-HISTORIAL: carga el snapshot de una version EN EL EDITOR (no guarda
  // solo). El usuario revisa y da Guardar, que crea una version nueva con lo restaurado.
  const restaurarVersion = useCallback(async (num) => {
    if (!cotIdLocal || !num) return;
    try {
      const r = await fetch(`${API_BASE_CLI}/api/cotizaciones/${cotIdLocal}/versiones/${num}`, { headers: authHeaders() }).then(x => x.json());
      if (!r || !r.ok || !r.version) throw new Error((r && r.error) || 'No se pudo cargar la version');
      const snap = r.version.snapshot || {};
      const cab = snap.cabecera || {};
      const prods = Array.isArray(snap.productos) ? snap.productos : [];
      const ls = prods.map((p, i) => {
        const costo = Number(p.costo_unitario) || 0;
        const precio = Number(p.precio_unitario) || 0;
        return {
          key: `v${num}_${i}`,
          nombre: p.nombre,
          sku: p.sku || null,
          tipo_catalogo: p.tipo || 'producto',
          imagen_url: p.imagen_url || null,
          cantidad: Math.max(1, Math.round(Number(p.cantidad) || 1)),
          costo_unitario: costo,
          precio_unitario: precio,
          precio_lista_mxn: p.precio_lista_mxn != null ? Number(p.precio_lista_mxn) : null,
          descuento_pct: 0,
          margen_pct: costo > 0 ? margenDePrecioCosto(precio, costo) : null,
        };
      });
      setLineas(ordenarLineas(ls));
      setNombre(cab.nombre || '');
      setNota(cab.notas_cliente || '');
      setMonedaCot(cab.moneda === 'USD' ? 'USD' : 'MXN');
      setHistAbierto(false);
      mostrarToast(`Version ${num} cargada. Guarda para aplicarla.`, 'ok');
    } catch (e) {
      mostrarToast('No se pudo restaurar la version. ' + (e.message || ''), 'error');
    }
  }, [cotIdLocal, mostrarToast]);

  // COTIZADOR-DUPLICAR: crea una copia (nuevo folio, mismo expediente, nombre unico)
  // y abre la COPIA en el editor reusando el evento global de abrir-editar.
  const [duplicando, setDuplicando] = useState(false);
  const duplicarCotizacion = useCallback(async () => {
    if (!cotIdLocal || duplicando) return;
    setDuplicando(true);
    try {
      const r = await fetch(`${API_BASE_CLI}/api/cotizaciones/${cotIdLocal}/duplicar`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ creado_por: usuario?.id || null }),
      }).then(x => x.json());
      if (r && r.ok && (r.cotizacion_id || r.id)) {
        mostrarToast(`Copia creada: ${r.folio || ''}. Lleva las mismas cláusulas, con el texto vigente del catálogo.`.trim(), 'ok');
        // Abre la copia en el editor (mismo mecanismo que el listado).
        window.dispatchEvent(new CustomEvent('beep-editar-cotizacion', { detail: { cotizacionId: r.cotizacion_id || r.id } }));
      } else {
        throw new Error((r && r.error) || 'No se pudo duplicar');
      }
    } catch (e) {
      mostrarToast('No se pudo duplicar la cotizacion. ' + (e.message || ''), 'error');
    } finally {
      setDuplicando(false);
    }
  }, [cotIdLocal, duplicando, usuario, mostrarToast]);

  // CO-ESTADOS: cambia SOLO el estado de la cotizacion (PATCH /:id/estado). No genera
  // version nueva (eso lo decide el backend). Refresca el chip con la respuesta.
  // CO-LINK-EXPEDIENTE: navega a la ficha del expediente vinculado. Reutiliza el mismo
  // patron de CustomEvent global que el resto del round-trip (Dashboard lo escucha,
  // cambia a la pagina de Expedientes y abre la ficha por id).
  const irAlExpediente = useCallback(() => {
    if (!expediente?.id) return;
    window.dispatchEvent(new CustomEvent('beep-abrir-expediente', { detail: { expedienteId: expediente.id } }));
  }, [expediente]);

  const cambiarEstado = useCallback(async (nuevo, motivo = null) => {
    if (!cotIdLocal || cambiandoEstado) return;
    if (nuevo === estadoCot) return; // sin cambio: no llamamos al backend
    setCambiandoEstado(true);
    try {
      // SPRINT DINERO 1: el backend firma con el usuario del token (ya no se manda
      // usuario_id); motivo solo viaja al des-autorizar.
      const r = await fetch(`${API_BASE_CLI}/api/cotizaciones/${cotIdLocal}/estado`, {
        method: 'PATCH',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ estado: nuevo, motivo: motivo || null }),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.ok && d.cotizacion) {
        setEstadoCot(d.cotizacion.estado);
        setEstadoEfectivo(d.cotizacion.estado_efectivo || d.cotizacion.estado);
        mostrarToast('Estado: ' + (ESTADO_COLOR[d.cotizacion.estado]?.label || d.cotizacion.estado), 'ok');
      } else {
        // Muestra el mensaje REAL del servidor (matriz de transiciones, rol, motivo, $0).
        throw new Error(d.error || 'No se pudo cambiar el estado');
      }
    } catch (e) {
      mostrarToast(e.message || 'No se pudo cambiar el estado', 'error');
    } finally {
      setCambiandoEstado(false);
    }
  }, [cotIdLocal, cambiandoEstado, estadoCot, mostrarToast]);

  // SPRINT DINERO 1: si la cotizacion esta AUTORIZADA, cualquier cambio de estado
  // pide motivo primero (modal); el resto de estados cambia directo como siempre.
  const pedirCambioEstado = useCallback((nuevo) => {
    if (nuevo === estadoCot) return;
    if (estadoCot === 'autorizada') {
      setMotivoTexto('');
      setMotivoModal({ destino: nuevo });
      return;
    }
    cambiarEstado(nuevo);
  }, [estadoCot, cambiarEstado]);

  // OPERACIONES Paso 1: enviar la cotizacion AUTORIZADA a almacen (crea la solicitud de
  // material + checklist). Idempotente: si ya existe, lo avisa (no duplica).
  const enviarAAlmacen = useCallback(async () => {
    if (!cotIdLocal || enviandoAlmacen) return;
    setEnviandoAlmacen(true);
    try {
      const r = await fetch(`${API_BASE_CLI}/api/solicitudes-material/desde-cotizacion/${cotIdLocal}`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ creada_por: usuario?.correo || null }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.ok) { mostrarToast(d.error || 'No se pudo enviar a almacen', 'error'); return; }
      const n = (d.items || []).length;
      mostrarToast(
        d.ya_existia
          ? `Esta cotizacion ya tenia solicitud de material (${d.solicitud.folio}).`
          : `Solicitud de material creada: ${d.solicitud.folio} · ${n} producto(s) para almacen.`,
        'ok'
      );
    } catch (e) {
      mostrarToast('No se pudo enviar a almacen. ' + (e.message || ''), 'error');
    } finally {
      setEnviandoAlmacen(false);
    }
  }, [cotIdLocal, enviandoAlmacen, usuario, mostrarToast]);

  // EXPORTAR-A-COLECCION: arma los items exportables (los que conservan su fuente) y
  // separa las lineas que no se pueden exportar (sin fuente, p.ej. cotizacion vieja).
  const construirItemsColeccion = useCallback(() => {
    const exportables = [];
    const omitidas = [];
    for (const l of lineas) {
      if (!l.fuente || !l.sku) { omitidas.push(l.nombre || l.sku || 'linea'); continue; }
      exportables.push({
        tipo: l.fuente,
        referencia_id: l.sku,
        nombre: l.nombre,
        marca: l.marca || null,
        modelo: l.modelo || null,
        imagen_url: l.imagen_url || null,
        // Para congelar (el backend ignora estos si congelar=false):
        precio_manual_mxn: Number(l.precio_unitario) || 0,
        costo_snapshot_mxn: Number(l.costo_unitario) || 0,
        margen_snapshot_pct: (l.margen_pct != null && l.margen_pct !== '') ? Number(l.margen_pct) : null,
      });
    }
    return { exportables, omitidas };
  }, [lineas]);

  const abrirModalCol = useCallback((congelar) => {
    setColNombre(nombre.trim() || '');
    setColIcono(congelar ? 'snowflake' : 'layers');
    setColColor('#5c9adb');
    setColDias(30);
    setModalCol({ congelar });
  }, [nombre]);

  const crearColeccion = useCallback(async () => {
    if (!modalCol) return;
    const nom = colNombre.trim();
    if (!nom) { mostrarToast('Ponle un nombre a la coleccion', 'warn'); return; }
    const { exportables, omitidas } = construirItemsColeccion();
    if (exportables.length === 0) {
      mostrarToast('Ninguna linea se puede exportar (se perdio su origen). Agregalas desde La Tienda.', 'warn');
      return;
    }
    setCreandoCol(true);
    try {
      const resp = await fetch(`${API_BASE_CLI}/api/colecciones/desde-cotizacion`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nombre: nom,
          icono: colIcono || null,
          color: colColor || null,
          congelar: modalCol.congelar === true,
          // Vigencia del congelado (dias). Solo aplica si congelar=true.
          dias_vigencia: modalCol.congelar ? (Number(colDias) || 30) : null,
          creada_por: usuario?.id || null,
          items: exportables,
        }),
      });
      const r = await resp.json().catch(() => ({}));
      if (!resp.ok || !r.ok) {
        mostrarToast(r.error || 'No se pudo crear la coleccion', 'error');
        return;
      }
      const tipoTxt = modalCol.congelar ? 'Lista congelada' : 'Catalogo';
      const aviso = omitidas.length > 0 ? ` (${omitidas.length} linea(s) sin origen no se incluyeron)` : '';
      mostrarToast(`${tipoTxt} "${nom}" creada con ${r.agregados} producto(s)${aviso}.`, 'ok');
      setModalCol(null);
    } catch (e) {
      mostrarToast('No se pudo crear la coleccion. ' + (e.message || ''), 'error');
    } finally {
      setCreandoCol(false);
    }
  }, [modalCol, colNombre, colIcono, colColor, colDias, construirItemsColeccion, usuario, mostrarToast]);

  const th = { fontSize: 11, fontWeight: 700, color: COLOR_GRIS_CLARO, textAlign: 'left', padding: '6px 8px', textTransform: 'uppercase', letterSpacing: 0.3 };
  const td = { fontSize: 13, color: COLOR_OSCURO, padding: '8px', borderTop: `1px solid ${COLOR_BORDE}`, verticalAlign: 'middle' };
  // Pulido: borde sutil + fondo suave + radius + alineado a la derecha. Uniforme.
  const campoTabla = {
    width: 80, padding: '7px 9px', border: `1px solid ${COLOR_BORDE}`, borderRadius: 8,
    background: tokens.surfaceSubtle, fontSize: 13, fontFamily: PP, color: COLOR_OSCURO,
    textAlign: 'right', outline: 'none',
  };
  const campoMovil = {
    padding: '10px 12px', border: `1px solid ${COLOR_BORDE}`, borderRadius: 10,
    background: tokens.surfaceSubtle, fontSize: 15, fontFamily: PP, color: COLOR_OSCURO,
    textAlign: 'right', outline: 'none',
  };
  const lbl = { fontSize: 11, color: COLOR_GRIS_CLARO, marginBottom: 6, fontWeight: 600 };

  return (
    <div style={{ fontFamily: PP, color: COLOR_GRIS, minHeight: '100%' }}>
      {/* Encabezado */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
        <button onClick={volver} aria-label={modoEditar ? 'Cotizaciones' : 'La Tienda'} style={{
          ...estiloBotonSecundario, display: 'inline-flex', alignItems: 'center', gap: 6,
        }}><ArrowLeft size={16} strokeWidth={2} /> {modoEditar ? 'Cotizaciones' : 'La Tienda'}</button>
        <div style={{ fontSize: 20, fontWeight: 700, color: COLOR_OSCURO }}>{modoEditar ? 'Editar cotizacion' : 'Previsualizar cotizacion'}</div>
        {/* Toggles de PRESENTACION: Con/Sin IVA + moneda (los datos se guardan SIN IVA y en MXN) */}
        <div style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <ToggleIva modo={ivaModo} onCambio={setIvaModo} compacto={isMobile} />
          <div style={{ display: 'inline-flex', border: `1px solid ${COLOR_BORDE}`, borderRadius: 99, overflow: 'hidden', background: '#fff' }}>
            {['MXN', 'USD'].map(m => (
              <button
                key={m} type="button" onClick={() => setMonedaCot(m)}
                style={{
                  padding: '6px 16px', fontSize: 12, fontWeight: 700, border: 'none', cursor: 'pointer', fontFamily: PP,
                  background: monedaCot === m ? COLOR_OSCURO : 'transparent',
                  color: monedaCot === m ? '#fff' : COLOR_GRIS,
                }}
              >{m}</button>
            ))}
          </div>
        </div>
      </div>
      {monedaCot === 'USD' && (
        <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: -10, marginBottom: 14 }}>
          Mostrando en USD al TC de venta ${Number(tc || 0).toFixed(2)}. Se guarda en MXN.
        </div>
      )}

      {cargando && <div style={{ color: COLOR_GRIS_CLARO }}>Cargando carrito…</div>}
      {!cargando && error && (
        <div style={{ ...estiloTarjeta, padding: 20, color: tokens.danger }}>{error}</div>
      )}

      {!cargando && !error && (
        <div style={{ display: 'grid', gap: 14, maxWidth: 1280 }}>
          {/* Nombre + expediente */}
          <div style={{ ...estiloTarjeta, padding: 18 }}>
            {/* CO-ESTADOS: estado de ESTA cotizacion (independiente del expediente). */}
            {modoEditar && (
              <div style={{ marginBottom: 16 }}>
                <div style={lbl}>Estado de la cotizacion</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{
                    display: 'inline-block', padding: '4px 12px', borderRadius: 99,
                    background: (ESTADO_COLOR[estadoEfectivo] || ESTADO_COLOR.borrador).bg,
                    color: (ESTADO_COLOR[estadoEfectivo] || ESTADO_COLOR.borrador).color,
                    fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap',
                  }}>{(ESTADO_COLOR[estadoEfectivo] || ESTADO_COLOR.borrador).label}</span>
                  {puedeCambiarEstado ? (
                    <select
                      value={estadoCot}
                      disabled={cambiandoEstado}
                      onChange={(e) => pedirCambioEstado(e.target.value)}
                      aria-label="Cambiar estado de la cotizacion"
                      style={{
                        padding: '8px 12px', borderRadius: 10, border: `1px solid ${COLOR_BORDE}`,
                        background: '#fff', fontSize: 13, fontFamily: PP, color: COLOR_OSCURO,
                        cursor: cambiandoEstado ? 'default' : 'pointer', minHeight: 40,
                        opacity: cambiandoEstado ? 0.6 : 1,
                      }}
                    >
                      {ESTADOS_OPCIONES.map(o => (
                        <option key={o.id} value={o.id}>{o.label}</option>
                      ))}
                    </select>
                  ) : (
                    <span style={{ fontSize: 11, color: COLOR_GRIS_CLARO }}>(solo lectura)</span>
                  )}
                </div>
                {estadoEfectivo === 'vencida' && (
                  <div style={{ fontSize: 11, color: '#9a3412', marginTop: 6 }}>
                    Vencida por fecha de vigencia. Cambia el estado para reactivarla.
                  </div>
                )}
                {esAutorizada && (
                  <div style={{
                    fontSize: 12, color: '#1D9E75', marginTop: 6, fontWeight: 700,
                    background: '#e8faf5', border: '1px solid #b8ecd9',
                    borderRadius: 8, padding: '6px 10px', display: 'inline-block',
                  }}>
                    Autorizada: para editar, regresala a borrador.
                  </div>
                )}
                {/* OPERACIONES Paso 1: enviar a almacen (solo cuando esta AUTORIZADA). */}
                {estadoEfectivo === 'autorizada' && (
                  <div style={{ marginTop: 10 }}>
                    <button
                      onClick={enviarAAlmacen}
                      disabled={enviandoAlmacen}
                      title="Crea la solicitud de material para almacen con los productos de esta cotizacion"
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 8,
                        background: tokens.primary, color: '#fff', border: 'none',
                        padding: '9px 16px', borderRadius: 8, fontSize: 13, fontWeight: 700,
                        cursor: enviandoAlmacen ? 'default' : 'pointer', fontFamily: PP,
                        opacity: enviandoAlmacen ? 0.6 : 1,
                      }}
                    >
                      <PackagePlus size={16} strokeWidth={2} /> {enviandoAlmacen ? 'Enviando…' : 'Enviar a almacen'}
                    </button>
                    <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 5 }}>
                      Genera la solicitud de material (checklist de surtido) para almacen.
                    </div>
                  </div>
                )}
              </div>
            )}

            <div style={lbl}>Nombre de la cotizacion</div>
            <input
              style={{ ...estiloInput, fontSize: 14 }}
              placeholder="Ej. CCTV opcion 1"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
            />

            <div style={{ ...lbl, marginTop: 16 }}>Expediente (opcional)</div>
            {expediente ? (
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                padding: '8px 12px', borderRadius: 10, background: tokens.primarySoft,
              }}>
                <FileText size={14} strokeWidth={2} color={COLOR_OSCURO} />
                <span style={{ fontSize: 13, fontWeight: 700, color: COLOR_OSCURO }}>
                  {expediente.cliente || 'Expediente'} · {expediente.folio}
                </span>
                <button
                  onClick={irAlExpediente}
                  title="Abrir la ficha de este expediente"
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: tokens.primary, fontSize: 12, fontWeight: 700, fontFamily: PP, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                ><ExternalLink size={13} strokeWidth={2.2} /> Ir al expediente</button>
                <button
                  onClick={irAElegirExpediente}
                  disabled={yendoExpediente}
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: tokens.primary, fontSize: 12, fontWeight: 700, fontFamily: PP, opacity: yendoExpediente ? 0.6 : 1 }}
                >{yendoExpediente ? 'Guardando…' : 'cambiar'}</button>
              </div>
            ) : (
              <button
                onClick={irAElegirExpediente}
                disabled={yendoExpediente}
                style={{ ...estiloBotonSecundario, display: 'inline-flex', alignItems: 'center', gap: 6, opacity: yendoExpediente ? 0.6 : 1 }}
              ><Link2 size={14} strokeWidth={2} /> {yendoExpediente ? 'Guardando…' : 'Vincular expediente'}</button>
            )}
          </div>

          {/* ITEMS — a todo lo ancho. Tabla en desktop, tarjetas en movil. Mismos datos y handlers. */}
          <div style={{ ...estiloTarjeta, padding: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: COLOR_OSCURO }}>
                Articulos ({calc.length})
              </div>
              <button
                onClick={() => { setSustituirIdx(null); setOrigenAbierto(true); }}
                disabled={yendoTienda || guardando || esAutorizada}
                title={esAutorizada ? 'Autorizada: para editar, regresala a borrador' : undefined}
                style={{
                  ...estiloBotonSecundario, display: 'inline-flex', alignItems: 'center', gap: 6,
                  color: COLOR_OSCURO, fontWeight: 700,
                  opacity: (yendoTienda || guardando || esAutorizada) ? 0.6 : 1,
                  cursor: esAutorizada ? 'not-allowed' : 'pointer',
                }}
              ><Plus size={16} strokeWidth={2.5} /> {yendoTienda ? 'Abriendo La Tienda…' : 'Agregar producto'}</button>
            </div>
            {/* MOTOR DE ORIGEN Fase 1: menu "de donde traer el producto". Solo navegacion;
                "La Tienda" ejecuta el flujo de hoy, las otras 2 quedan en "Proximamente". */}
            {origenAbierto && (
              <ModalOrigenProducto
                // EDITAR -> nuevo "modo agregar a cotizacion" (sin carrito), navega al tab.
                // NUEVA  -> flujo viejo del carrito INTACTO (La Tienda / coleccion F2).
                onElegirTienda={() => { setOrigenAbierto(false); modoEditar ? entrarModoAgregar('cotizaciones.tienda', sustituirIdx) : agregarEnTienda(); }}
                onElegirColeccion={() => { setOrigenAbierto(false); modoEditar ? entrarModoAgregar('cotizaciones.colecciones', sustituirIdx) : irAColeccionOrigen(); }}
                // Listas: habilitada SOLO en EDITAR (en NUEVA queda "Proximamente").
                onElegirListas={modoEditar ? () => { setOrigenAbierto(false); entrarModoAgregar('cotizaciones.listas', sustituirIdx); } : undefined}
                onCerrar={() => setOrigenAbierto(false)}
              />
            )}
            {/* Fase 2: navegador de coleccion. El carrito ya quedo sembrado; al terminar de
                agregar, volverACotizacion regresa al editor con las lineas originales + nuevas. */}
            {colOrigen && (
              <ModalColeccionOrigen
                onTerminado={() => {
                  const { carritoId, cotizacionId } = colOrigen;
                  setColOrigen(null);
                  volverACotizacion(carritoId, cotizacionId);
                }}
                onCerrar={() => setColOrigen(null)}
              />
            )}
            {calc.length === 0 && (
              <div style={{ color: COLOR_GRIS_CLARO, fontSize: 13 }}>No hay articulos. Agrega productos desde La Tienda.</div>
            )}

            {/* DnD #4a: arrastrar para reordenar (tabla en desktop, tarjetas en compacto). El
                handle (≡) arrastra; el resto de la fila/tarjeta sigue editable. */}
            <DndContext sensors={sensoresDnD} collisionDetection={closestCenter} onDragEnd={esAutorizada ? undefined : onDragEndLinea}>
            {/* DESKTOP HORIZONTAL (ancho real >=1100): tabla */}
            {calc.length > 0 && !compacto && (
              <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ ...th, width: 28 }}></th>
                      <th style={th}>Articulo</th>
                      <th style={{ ...th, textAlign: 'right' }}>Cantidad</th>
                      <th style={{ ...th, textAlign: 'right' }}>Lista</th>
                      <th style={{ ...th, textAlign: 'right' }}>Costo</th>
                      <th style={{ ...th, textAlign: 'right' }}>Margen %</th>
                      <th style={{ ...th, textAlign: 'right' }}>Precio venta</th>
                      <th style={{ ...th, textAlign: 'right' }}>Subtotal</th>
                      <th style={{ ...th, textAlign: 'right' }}>Utilidad</th>
                      <th style={th}></th>
                    </tr>
                  </thead>
                  <tbody>
                    <SortableContext items={calc.map(l => l.key)} strategy={verticalListSortingStrategy}>
                    {calc.map(l => {
                      const inst = esInstalacion(l.tipo_catalogo);
                      return (
                      <SortableRowCotizador key={l.key} id={l.key} inst={inst} bloqueado={esAutorizada}>
                        <td style={{ ...td, minWidth: 260 }}>
                          {/* Nombre + imagen clickeables -> ficha completa (FICHA-PRODUCTO). */}
                          <ProductoInfoClickeable l={l} thumbSize={64} inst={inst} onVer={verFicha} />
                        </td>
                        <td style={{ ...td, textAlign: 'right' }}>
                          {esAutorizada ? (
                            <span style={{ fontWeight: 700 }}>{l.cantidad}</span>
                          ) : (
                            <input
                              type="number" min="1" step="1" style={campoTabla}
                              value={l.cantidad}
                              onChange={e => setLinea(l.key, 'cantidad', e.target.value)}
                            />
                          )}
                        </td>
                        <td style={{ ...td, textAlign: 'right', color: COLOR_GRIS_CLARO, whiteSpace: 'nowrap' }}>
                          {l.precio_lista_mxn != null ? fmt(conIvaSegun(ivaModo, l.precio_lista_mxn)) : '—'}
                        </td>
                        <td style={{ ...td, textAlign: 'right', color: COLOR_GRIS_CLARO, whiteSpace: 'nowrap' }}>{fmt(conIvaSegun(ivaModo, l.costo_unitario))}</td>
                        <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                          {esAutorizada ? (
                            <span>{(Number(l.margen_pct) || 0).toFixed(1)}%</span>
                          ) : (
                            <InputMargen valor={l.margen_pct} costo={l.costo_unitario} onChange={(v) => onMargenChange(l.key, v)} style={{ ...campoTabla, width: 70 }} />
                          )}
                        </td>
                        <td style={{ ...td, textAlign: 'right' }}>
                          {esAutorizada ? (
                            <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{fmt(conIvaSegun(ivaModo, l.precio_unitario))}</span>
                          ) : (
                            <InputPrecio valorMXN={conIvaSegun(ivaModo, l.precio_unitario)} onCommit={(v) => onPrecioChange(l.key, quitarIvaSegun(ivaModo, v))} style={{ ...campoTabla, width: 100 }} />
                          )}
                        </td>
                        <td style={{ ...td, textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' }}>{fmt(conIvaSegun(ivaModo, l.sub))}</td>
                        <td style={{ ...td, textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap', color: (l.sub - l.costo) >= 0 ? tokens.success : tokens.danger }}>
                          {(l.sub - l.costo) >= 0 ? fmtMargenLinea(l.sub - l.costo) : fmt(l.sub - l.costo)}
                        </td>
                        <td style={{ ...td, textAlign: 'center', whiteSpace: 'nowrap' }}>
                          {!esAutorizada && modoEditar && (
                            <button
                              onClick={() => pedirSustituir(l.key)}
                              aria-label="Sustituir producto"
                              title="Sustituir por otro producto (conserva la cantidad)"
                              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: tokens.primary, padding: 6 }}
                            ><Repeat size={16} strokeWidth={2} /></button>
                          )}
                          {!esAutorizada && (
                            <button
                              onClick={() => quitarLinea(l.key)}
                              aria-label="Quitar"
                              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: tokens.danger, padding: 6 }}
                            ><Trash2 size={16} strokeWidth={2} /></button>
                          )}
                        </td>
                      </SortableRowCotizador>
                      );
                    })}
                    </SortableContext>
                  </tbody>
                </table>
              </div>
            )}

            {/* ANGOSTO (celular, tablet, PC VERTICAL): tarjetas verticales (sin scroll horizontal) */}
            {calc.length > 0 && compacto && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <SortableContext items={calc.map(l => l.key)} strategy={verticalListSortingStrategy}>
                {calc.map(l => {
                  const inst = esInstalacion(l.tipo_catalogo);
                  return (
                  <SortableCardCotizador key={l.key} id={l.key} inst={inst} bloqueado={esAutorizada}>
                    {/* cabecera: nombre + imagen clickeables -> ficha (FICHA-PRODUCTO). */}
                    <ProductoInfoClickeable l={l} thumbSize={56} inst={inst} onVer={verFicha} />
                    {/* lista + costo (solo lectura) */}
                    <div style={{ display: 'flex', gap: 16, fontSize: 12, color: COLOR_GRIS_CLARO }}>
                      <span>Lista: {l.precio_lista_mxn != null ? fmt(conIvaSegun(ivaModo, l.precio_lista_mxn)) : '—'}</span>
                      <span>Costo: {fmt(conIvaSegun(ivaModo, l.costo_unitario))}</span>
                    </div>
                    {/* controles tactiles */}
                    <FilaControl label="Cantidad">
                      {esAutorizada ? (
                        <span style={{ fontSize: 15, fontWeight: 700 }}>{l.cantidad}</span>
                      ) : (
                        <Stepper
                          valor={l.cantidad}
                          onMenos={() => setLinea(l.key, 'cantidad', Math.max(1, (Math.round(Number(l.cantidad) || 1)) - 1))}
                          onMas={() => setLinea(l.key, 'cantidad', (Math.round(Number(l.cantidad) || 1)) + 1)}
                          onSet={(v) => setLinea(l.key, 'cantidad', v)}
                        />
                      )}
                    </FilaControl>
                    <FilaControl label="Margen %">
                      {esAutorizada ? (
                        <span style={{ fontSize: 15 }}>{(Number(l.margen_pct) || 0).toFixed(1)}%</span>
                      ) : (
                        <InputMargen valor={l.margen_pct} costo={l.costo_unitario} onChange={(v) => onMargenChange(l.key, v)} style={{ ...campoMovil, width: 90 }} />
                      )}
                    </FilaControl>
                    <FilaControl label="Precio venta">
                      {esAutorizada ? (
                        <span style={{ fontSize: 15, fontWeight: 700 }}>{fmt(conIvaSegun(ivaModo, l.precio_unitario))}</span>
                      ) : (
                        <InputPrecio valorMXN={conIvaSegun(ivaModo, l.precio_unitario)} onCommit={(v) => onPrecioChange(l.key, quitarIvaSegun(ivaModo, v))} style={{ ...campoMovil, width: 130 }} />
                      )}
                    </FilaControl>
                    <FilaControl label="Utilidad">
                      <span style={{ fontSize: 15, fontWeight: 700, color: (l.sub - l.costo) >= 0 ? tokens.success : tokens.danger }}>
                        {(l.sub - l.costo) >= 0 ? fmtMargenLinea(l.sub - l.costo) : fmt(l.sub - l.costo)}
                      </span>
                    </FilaControl>
                    {/* pie */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', borderTop: `1px solid ${COLOR_BORDE}`, paddingTop: 8 }}>
                      <span style={{ fontSize: 16, fontWeight: 800, color: COLOR_OSCURO }}>{fmt(conIvaSegun(ivaModo, l.sub))}</span>
                      {!esAutorizada && (
                        <div style={{ display: 'flex', gap: 6 }}>
                          {modoEditar && (
                            <button
                              onClick={() => pedirSustituir(l.key)}
                              title="Sustituir por otro producto (conserva la cantidad)"
                              style={{ ...estiloBotonSecundario, color: tokens.primary, display: 'inline-flex', alignItems: 'center', gap: 6 }}
                            ><Repeat size={14} strokeWidth={2} /> Sustituir</button>
                          )}
                          <button
                            onClick={() => quitarLinea(l.key)}
                            aria-label="Quitar"
                            style={{ ...estiloBotonSecundario, color: tokens.danger, display: 'inline-flex', alignItems: 'center', gap: 6 }}
                          ><Trash2 size={14} strokeWidth={2} /> Quitar</button>
                        </div>
                      )}
                    </div>
                  </SortableCardCotizador>
                  );
                })}
                </SortableContext>
              </div>
            )}
            </DndContext>
          </div>

          {/* DESGLOSE ABAJO. Desktop: Desglose (interno) IZQUIERDA, Totales DERECHA via row-reverse
              (el DOM conserva Totales primero -> en movil 'column' deja Totales arriba, sin cambios). */}
          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row-reverse', gap: 14, alignItems: 'stretch' }}>
            {/* Totales — lo que paga el cliente. Se adapta al switch para CUADRAR con
                las columnas: en modo 'con' el numero grande es el Total con IVA (= suma
                de los subtotales por linea con IVA); en modo 'sin' se ve el desglose. */}
            <div style={{ ...estiloTarjeta, padding: 18, flex: '1 1 320px', minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 12 }}>Totales</div>
              {ivaModo === 'con' ? (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <span style={{ fontSize: 15, fontWeight: 800, color: COLOR_OSCURO }}>Total (con IVA)</span>
                    <span style={{ fontSize: 20, fontWeight: 800, color: COLOR_OSCURO }}>{fmt(total)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', fontSize: 12, color: COLOR_GRIS_CLARO, marginTop: 4 }}>
                    IVA 16% incluido: {fmt(iva)}
                  </div>
                </>
              ) : (
                <>
                  <Casc label="Subtotal (sin IVA)" valor={fmt(subtotal)} />
                  <Casc label="IVA (16%)" valor={fmt(iva)} />
                  <div style={{ borderTop: `2px solid ${COLOR_BORDE}`, margin: '10px 0' }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <span style={{ fontSize: 15, fontWeight: 800, color: COLOR_OSCURO }}>Total con IVA</span>
                    <span style={{ fontSize: 20, fontWeight: 800, color: COLOR_OSCURO }}>{fmt(total)}</span>
                  </div>
                </>
              )}
            </div>

            {/* Desglose de costos y utilidades — INTERNO */}
            <div style={{
              ...estiloTarjeta, padding: 18, flex: '1 1 320px', minWidth: 0,
              background: tokens.primarySoft, border: `1px solid ${tokens.primary}`,
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: tokens.primary, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 2 }}>Interno</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 12 }}>Desglose de costos y utilidades</div>
              <Casc label="Precio de venta total (con IVA)" valor={fmt(total)} fuerte />
              <Casc label="IVA (16%)" valor={`- ${fmt(iva)}`} resta sangria />
              <Casc label="Venta neta (sin IVA)" valor={fmt(subtotal)} fuerte />
              <Casc label="Productos (proveedores)" valor={`- ${fmt(costoProd)}`} resta sangria />
              <Casc label="Instalacion / mano de obra" valor={`- ${fmt(costoInst)}`} resta sangria />
              <div style={{ borderTop: `2px solid ${COLOR_BORDE}`, margin: '10px 0' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span style={{ fontSize: 15, fontWeight: 800, color: COLOR_OSCURO }}>UTILIDAD NETA</span>
                <span style={{ fontSize: 20, fontWeight: 800, color: utilidadNeta > 0 ? tokens.success : tokens.danger }}>
                  {fmt(utilidadNeta)}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', fontSize: 12, color: COLOR_GRIS_CLARO, marginTop: 2 }}>
                Margen neto {margenNeto.toFixed(1)}%
              </div>
            </div>
          </div>

          {/* Nota / observaciones (se guarda en notas_cliente) */}
          <div style={{ ...estiloTarjeta, padding: 18 }}>
            <div style={lbl}>Nota / observaciones</div>
            <textarea
              style={{ ...estiloInput, minHeight: 80, resize: 'vertical', fontFamily: PP }}
              placeholder="Notas u observaciones para esta cotizacion…"
              value={nota}
              onChange={(e) => setNota(e.target.value)}
            />
          </div>

          {/* DOCUMENTO DE 3 PAGINAS: condiciones de pago, cuentas, switches y garantia por linea */}
          <div style={{ ...estiloTarjeta, padding: 18 }}>
            <div style={{ ...lbl, fontSize: 12 }}>Condiciones del documento (páginas 2 y 3 del PDF)</div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14 }}>
              <div>
                <div style={lbl}>Anticipo</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {[100, 80, 70, 50, -1].map(v => (
                    <button key={v} type="button" onClick={() => setAnticipoPct(v)}
                      style={{ padding: '10px 14px', minHeight: 44, borderRadius: 10, border: `1px solid ${COLOR_BORDE}`, fontFamily: PP, fontSize: 13, fontWeight: 700, cursor: 'pointer',
                        background: anticipoPct === v ? COLOR_OSCURO : '#fff', color: anticipoPct === v ? '#fff' : COLOR_GRIS }}>
                      {v === -1 ? 'Otro' : `${v}%`}
                    </button>
                  ))}
                  {anticipoPct === -1 && (
                    <input style={{ ...estiloInput, width: 90, minHeight: 44, fontSize: 16 }} type="number" min="1" max="100" inputMode="numeric" value={anticipoOtro} onChange={e => setAnticipoOtro(e.target.value)} placeholder="%" aria-label="Porcentaje de anticipo" />
                  )}
                </div>
                {/* TERMINOS-COTIZACION: cuando se paga el saldo (solo con anticipo menor al 100%) */}
                {anticipoEfectivo >= 1 && anticipoEfectivo < 100 && (
                  <div style={{ marginTop: 10 }}>
                    <div style={lbl}>Saldo ({100 - anticipoEfectivo}%): cuándo se paga</div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {[[null, 'Contra entrega'], [15, '15 días'], [30, '30 días'], [-1, 'Otro']].map(([v, t]) => (
                        <button key={String(v)} type="button" onClick={() => setSaldoModo(v)} aria-pressed={saldoModo === v}
                          style={{ padding: '10px 14px', minHeight: 44, borderRadius: 10, border: `1px solid ${COLOR_BORDE}`, fontFamily: PP, fontSize: 13, fontWeight: 700, cursor: 'pointer',
                            background: saldoModo === v ? COLOR_OSCURO : '#fff', color: saldoModo === v ? '#fff' : COLOR_GRIS }}>
                          {t}
                        </button>
                      ))}
                      {saldoModo === -1 && (
                        <input style={{ ...estiloInput, width: 90, minHeight: 44, fontSize: 16 }} type="number" min="1" max="365" inputMode="numeric" value={saldoOtro} onChange={e => setSaldoOtro(e.target.value)} placeholder="días" aria-label="Días para pagar el saldo" />
                      )}
                    </div>
                  </div>
                )}
                <div style={{ fontSize: 11, color: errorCondiciones ? '#b42318' : COLOR_GRIS_CLARO, marginTop: 6 }}>
                  {errorCondiciones || (anticipoEfectivo >= 100 ? 'Se imprime: 100% a la confirmación del pedido.'
                    : `Se imprime: ${anticipoEfectivo}% de anticipo, ${100 - anticipoEfectivo}% ${saldoDiasEfectivo ? `a ${saldoDiasEfectivo} días de la entrega` : 'contra entrega'}.`)}
                </div>
              </div>
              <div>
                <div style={lbl}>Tiempo de entrega</div>
                <input style={{ ...estiloInput, minHeight: 44, fontSize: 16 }} value={tiempoEntrega} onChange={e => setTiempoEntrega(e.target.value)} placeholder="Ej. 10 días hábiles tras el anticipo" />
                <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginTop: 12, fontSize: 13 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 44, cursor: 'pointer' }}><input type="checkbox" style={{ width: 20, height: 20 }} checked={incluirPago} onChange={e => setIncluirPago(e.target.checked)} /> Incluir página de pago</label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 44, cursor: 'pointer' }}><input type="checkbox" style={{ width: 20, height: 20 }} checked={incluirCert} onChange={e => setIncluirCert(e.target.checked)} /> Incluir certificado de garantía</label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 44, cursor: 'pointer' }}><input type="checkbox" style={{ width: 20, height: 20 }} checked={mostrarDescripcion} onChange={e => { setMostrarDescripcion(e.target.checked); setDescripcionDefinida(true); }} /> Mostrar descripción en el PDF</label>
                </div>
              </div>
            </div>
            {empresa && Array.isArray(empresa.cuentas) && empresa.cuentas.filter(c => c.mostrar_en_cotizaciones).length > 0 && incluirPago && (
              <div style={{ marginTop: 12 }}>
                <div style={lbl}>Cuentas bancarias a mostrar</div>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  {empresa.cuentas.filter(c => c.mostrar_en_cotizaciones).map(c => {
                    const marcada = cuentasSel ? cuentasSel.includes(c.id) : !!c.principal;
                    return (
                      <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 44, cursor: 'pointer', fontSize: 13, border: `1px solid ${COLOR_BORDE}`, borderRadius: 10, padding: '0 12px' }}>
                        <input type="checkbox" style={{ width: 20, height: 20 }} checked={marcada} onChange={e => {
                          const base = cuentasSel ? [...cuentasSel] : empresa.cuentas.filter(x => x.principal).map(x => x.id);
                          setCuentasSel(e.target.checked ? [...new Set([...base, c.id])] : base.filter(x => x !== c.id));
                        }} />
                        {c.banco} · {c.moneda}{c.principal ? ' · principal' : ''}
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
            {incluirCert && lineas.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div style={lbl}>Garantía por línea (se imprime en el certificado; edita si aplica)</div>
                <div style={{ display: 'grid', gap: 6 }}>
                  {lineas.map(l => {
                    const g = (l.lineaId != null && garantiasLinea[l.lineaId]) || {};
                    const esServ = esInstalacion(l.tipo_catalogo);
                    const valor = g.garantia_dias && !g.garantia_meses ? `${g.garantia_dias} días` : (g.garantia_meses != null ? fmtGarantiaTexto(g.garantia_meses, null) : (esServ ? 'Beep (según configuración)' : 'según catálogo'));
                    return (
                      <div key={l.key} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 12, padding: '6px 10px', borderRadius: 8, background: esServ ? '#EAF7F0' : tokens.surfaceSubtle }}>
                        <span style={{ flex: '1 1 220px', color: COLOR_OSCURO, fontWeight: 600 }}>{l.nombre}</span>
                        <span style={{ color: COLOR_GRIS_CLARO }}>{esServ ? 'mano de obra' : (g.garantia_origen || 'fabricante')}</span>
                        {l.lineaId != null ? (
                          <>
                            <input type="number" min="0" inputMode="numeric" style={{ ...estiloInput, width: 84, minHeight: 40, fontSize: 15 }} value={g.garantia_meses ?? ''} placeholder="meses"
                              onChange={e => setGarantiasLinea(prev => ({ ...prev, [l.lineaId]: { ...(prev[l.lineaId] || {}), garantia_meses: e.target.value === '' ? null : Number(e.target.value), garantia_dias: null, garantia_origen: esServ ? 'mano de obra' : 'fabricante', editada: true } }))} />
                            <span style={{ color: COLOR_GRIS_CLARO }}>meses</span>
                            <span style={{ color: COLOR_GRIS, minWidth: 70 }}>{valor}</span>
                          </>
                        ) : <span style={{ color: COLOR_GRIS_CLARO }}>Guarda la cotización para editar</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              {esAutorizada ? (
                <>
                  <button type="button" disabled={guardandoCond || !cotIdLocal}
                    onClick={async () => { const r = await guardarCondiciones(); mostrarToast(r.ok ? 'Condiciones del documento guardadas. Ya salen en el PDF.' : (r.condicionesGuardadas ? 'Condiciones guardadas, pero la garantía por línea no se pudo cambiar: ' + (r.error || '') : 'No se guardaron: ' + (r.error || '')), r.ok ? 'ok' : 'error'); }}
                    style={{ padding: '10px 16px', minHeight: 44, borderRadius: 10, border: 'none', background: COLOR_OSCURO, color: '#fff', fontFamily: PP, fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: guardandoCond ? 0.6 : 1 }}>
                    {guardandoCond ? 'Guardando…' : 'Guardar condiciones del documento'}
                  </button>
                  <span style={{ fontSize: 12, color: COLOR_GRIS_CLARO }}>Se pueden editar aunque la cotización esté autorizada: son condiciones de presentación, no tocan precios ni totales.</span>
                </>
              ) : (
                <span style={{ fontSize: 12, color: COLOR_GRIS_CLARO }}>Se guardan con «Guardar cotización» (o al descargar el PDF).</span>
              )}
            </div>
            {certInfo && (
              <div style={{ marginTop: 10, fontSize: 12, color: COLOR_GRIS }}>Certificado <strong>{certInfo.folio}</strong> · código <span style={{ fontFamily: 'monospace' }}>{certInfo.codigo}</span> · <a href={certInfo.url} target="_blank" rel="noreferrer" style={{ color: '#265F97' }}>ver registro oficial</a></div>
            )}
            {/* QR oculto: qrcode.react lo dibuja y el PDF lo lee como PNG */}
            <div ref={qrRef} style={{ position: 'absolute', left: -9999, top: -9999 }} aria-hidden="true">
              {certInfo && <QRCodeCanvas value={certInfo.url} size={512} marginSize={1} level="H" imageSettings={{ src: '/isotipo-beep.png', width: 104, height: 104, excavate: true }} />}
            </div>
          </div>

          {/* TERMINOS-COTIZACION: terminos y condiciones (al final del PDF) */}
          <TerminosCotizacion
            catalogo={catClausulas}
            foto={fotoTerminos}
            seleccion={selTerminos}
            onCambiar={(ids) => { setTerminosTocados(true); setSelTerminos(ids); }}
            onActivar={activarTerminos}
            soloLectura={esAutorizada}
            vigenciaDias={vigenciaDias}
            anticipoPct={anticipoEfectivo}
            saldoDias={saldoDiasEfectivo}
            duplicadaDeFolio={duplicadaDeFolio}
            familiasTexto={familiasSugeridas.map(f => (familiasCat.find(x => x.id === f) || {}).etiqueta || f).join(', ')}
            cargando={cargandoTerminos}
          />

          {/* Acciones (debajo de ambas tarjetas + nota): Guardar + Descargar PDF.
              RESPONSIVE B-3: sticky al fondo del scrollport (fondo solido + borde/sombra
              arriba) para que SIEMPRE se vean y alcancen, sin importar el alto ni la
              orientacion (clave en PC vertical). Sigue con flexWrap:wrap -> en angosto los
              botones se apilan/2 por fila solos. */}
          <div style={{
            display: 'flex', gap: 10, flexWrap: 'wrap',
            position: 'sticky', bottom: 0, zIndex: 20,
            background: COLOR_FONDO, borderTop: `1px solid ${COLOR_BORDE}`,
            boxShadow: '0 -4px 12px rgba(0,0,0,0.06)', padding: '12px 0 4px',
          }}>
            <button
              onClick={guardar}
              disabled={lineas.length === 0 || guardando || esAutorizada}
              title={esAutorizada ? 'Autorizada: para editar, regresala a borrador' : undefined}
              style={{
                ...estiloBotonPrimario, flex: '2 1 220px', background: tokens.success,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                opacity: (lineas.length === 0 || guardando || esAutorizada) ? 0.6 : 1,
                cursor: (lineas.length === 0 || guardando || esAutorizada) ? 'not-allowed' : 'pointer',
              }}
            ><FileText size={16} strokeWidth={2} /> {guardando ? 'Guardando…' : 'Guardar cotizacion'}</button>
            <button
              onClick={descargarPDF}
              disabled={lineas.length === 0 || generandoPDF}
              style={{
                ...estiloBotonSecundario, flex: '1 1 180px',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                opacity: (lineas.length === 0 || generandoPDF) ? 0.6 : 1,
              }}
            ><Download size={16} strokeWidth={2} /> {generandoPDF ? 'Generando PDF…' : 'Descargar PDF'}</button>
            {/* CARTA DE DATOS BANCARIOS: acompaña a la pagina de pago. Toma la vigencia de esta
                cotizacion y queda ligada a ella (si generas otra, la anterior se revoca sola). */}
            {cotIdLocal && (
              <button
                onClick={() => setCartaAbierta(true)}
                title="Generar la carta de datos bancarios con la vigencia de esta cotizacion"
                style={{
                  ...estiloBotonSecundario, flex: '1 1 220px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}
              ><Landmark size={16} strokeWidth={2} /> Carta de datos bancarios</button>
            )}
            {modoEditar && (
              <button
                onClick={() => setHistAbierto(true)}
                style={{
                  ...estiloBotonSecundario, flex: '1 1 160px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}
              ><Clock size={16} strokeWidth={2} /> Historial</button>
            )}
            {modoEditar && (
              <button
                onClick={duplicarCotizacion}
                disabled={duplicando}
                style={{
                  ...estiloBotonSecundario, flex: '1 1 160px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  opacity: duplicando ? 0.6 : 1,
                }}
              ><Copy size={16} strokeWidth={2} /> {duplicando ? 'Duplicando…' : 'Duplicar'}</button>
            )}
            {/* EXPORTAR-A-COLECCION: guardar estos productos como coleccion (catalogo o
                lista congelada). Aditivo: no afecta el flujo de la cotizacion. */}
            <button
              onClick={() => abrirModalCol(false)}
              disabled={lineas.length === 0}
              title="Guardar estos productos como una coleccion (precios dinamicos)"
              style={{
                ...estiloBotonSecundario, flex: '1 1 170px',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                opacity: lineas.length === 0 ? 0.6 : 1,
              }}
            ><Layers size={16} strokeWidth={2} /> Crear catalogo</button>
            <button
              onClick={() => abrirModalCol(true)}
              disabled={lineas.length === 0}
              title="Guardar estos productos como coleccion con los precios actuales CONGELADOS"
              style={{
                ...estiloBotonSecundario, flex: '1 1 190px',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                color: COLOR_OSCURO, fontWeight: 700,
                opacity: lineas.length === 0 ? 0.6 : 1,
              }}
            ><Snowflake size={16} strokeWidth={2} /> Crear lista congelada</button>
          </div>
        </div>
      )}

      <ModalCartaBancaria
        abierto={cartaAbierta}
        onCerrar={() => setCartaAbierta(false)}
        cotizacionId={cotIdLocal}
        folioCotizacion={cabecera?.folio || null}
      />

      <ModalHistorial
        abierto={histAbierto}
        cotizacionId={cotIdLocal}
        onCerrar={() => setHistAbierto(false)}
        onRestaurar={restaurarVersion}
        formatear={fmt}
      />

      {/* EXPORTAR-A-COLECCION: modal de nombre + icono/color */}
      {modalCol && (
        <div
          onClick={() => !creandoCol && setModalCol(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 1700, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '6vh 12px', overflowY: 'auto' }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 14, width: 'min(460px, 96vw)', padding: 22, fontFamily: PP, boxShadow: '0 16px 48px rgba(0,0,0,0.25)' }}
          >
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 17, fontWeight: 800, color: COLOR_OSCURO, marginBottom: 4 }}>
              {modalCol.congelar ? <Snowflake size={18} /> : <Layers size={18} />}
              {modalCol.congelar ? 'Crear lista congelada' : 'Crear catalogo'}
            </div>
            <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, marginBottom: 16, lineHeight: 1.4 }}>
              {modalCol.congelar
                ? 'Guarda estos productos como coleccion con los precios actuales CONGELADOS (el precio venta de cada linea).'
                : 'Guarda estos productos como coleccion. Los precios siguen siendo dinamicos (varian con el tipo de cambio).'}
            </div>

            <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, fontWeight: 600, marginBottom: 5 }}>Nombre</div>
            <input
              autoFocus
              value={colNombre}
              onChange={(e) => setColNombre(e.target.value)}
              placeholder="Ej. Kit CCTV recomendado"
              style={{ ...estiloInput, fontSize: 14, marginBottom: 14 }}
            />

            {modalCol.congelar && (
              <>
                <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, fontWeight: 600, marginBottom: 5 }}>Dias de vigencia del precio congelado</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                  <input
                    type="number" min="1" step="1"
                    value={colDias}
                    onChange={(e) => setColDias(e.target.value)}
                    style={{ ...estiloInput, fontSize: 14, marginBottom: 0, width: 110 }}
                  />
                  <span style={{ fontSize: 12, color: COLOR_GRIS_CLARO }}>
                    dias (vence el {(() => { const n = Number(colDias) || 0; const d = new Date(); d.setDate(d.getDate() + n); return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }); })()})
                  </span>
                </div>
              </>
            )}

            <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, fontWeight: 600, marginBottom: 6 }}>Color</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
              {['#93C5FD', '#3B82F6', '#5c9adb', '#5cd4b0', '#e0a05c', '#9a5cdb', '#e05c5c', '#f5b041', '#2f5f9e'].map(c => (
                <button key={c} type="button" onClick={() => setColColor(c)} title={c}
                  style={{ width: 24, height: 24, borderRadius: 6, background: c, cursor: 'pointer', border: colColor === c ? `2px solid ${COLOR_OSCURO}` : `1px solid ${COLOR_BORDE}` }} />
              ))}
            </div>

            <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, fontWeight: 600, marginBottom: 6 }}>Icono</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
              <span style={{ width: 36, height: 36, borderRadius: 9, flexShrink: 0, background: (colColor || '#5c9adb') + '22', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                <IconoLucide nombre={colIcono} size={18} color={colColor || '#5c9adb'} />
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <SelectorIconoLucide value={colIcono} onChange={(n) => setColIcono(n)} />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setModalCol(null)} disabled={creandoCol} style={{ ...estiloBotonSecundario }}>Cancelar</button>
              <button onClick={crearColeccion} disabled={creandoCol} style={{ ...estiloBotonPrimario, background: tokens.success, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                {creandoCol ? 'Creando…' : (modalCol.congelar ? 'Crear lista congelada' : 'Crear catalogo')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SPRINT DINERO 1: modal de motivo para transiciones DESDE autorizada.
          Responsive: ancho min(94vw,460px), maxHeight 85vh, botones >=44px, textarea 16px. */}
      {motivoModal && (
        <div
          onClick={() => !cambiandoEstado && setMotivoModal(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1700,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 14, padding: 20, width: 'min(94vw, 460px)',
              maxHeight: 'calc(var(--beep-vh) * 85)', overflowY: 'auto', boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
              fontFamily: PP, boxSizing: 'border-box',
            }}
          >
            <div style={{ fontSize: 16, fontWeight: 800, color: COLOR_OSCURO, marginBottom: 4 }}>
              Modificar cotizacion autorizada
            </div>
            <div style={{ fontSize: 13, color: COLOR_GRIS_CLARO, marginBottom: 12 }}>
              Vas a cambiarla a "{ESTADO_COLOR[motivoModal.destino]?.label || motivoModal.destino}".
              Indica el motivo (queda registrado en el historial).
            </div>
            <textarea
              value={motivoTexto}
              onChange={(e) => setMotivoTexto(e.target.value)}
              placeholder="Motivo del cambio (obligatorio)"
              rows={3}
              autoFocus
              style={{
                width: '100%', boxSizing: 'border-box', padding: '12px 14px', fontSize: 16,
                fontFamily: PP, border: `1px solid ${COLOR_BORDE}`, borderRadius: 10,
                resize: 'vertical', minHeight: 88, outline: 'none', color: COLOR_OSCURO,
              }}
            />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 14, flexWrap: 'wrap' }}>
              <button
                onClick={() => setMotivoModal(null)}
                disabled={cambiandoEstado}
                style={{ ...estiloBotonSecundario, minHeight: 44 }}
              >Cancelar</button>
              <button
                onClick={() => {
                  const m = motivoTexto.trim();
                  if (!m) return;
                  cambiarEstado(motivoModal.destino, m);
                  setMotivoModal(null);
                }}
                disabled={cambiandoEstado || !motivoTexto.trim()}
                style={{
                  ...estiloBotonPrimario, minHeight: 44,
                  opacity: (cambiandoEstado || !motivoTexto.trim()) ? 0.6 : 1,
                }}
              >{cambiandoEstado ? 'Cambiando…' : 'Confirmar cambio'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Toast: aviso flotante temporal (la cotizacion sigue abierta). Tipo define
          color/posicion/tamano: ok = sutil abajo; warn/error = grande, centrado arriba. */}
      {toast && (toast.tipo === 'ok' ? (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          background: '#1a3a5c', color: '#fff', padding: '12px 20px', borderRadius: 12,
          fontSize: 13, fontWeight: 600, fontFamily: PP, zIndex: 1600,
          boxShadow: '0 8px 24px rgba(0,0,0,0.2)', display: 'inline-flex', alignItems: 'center', gap: 8,
        }}>
          <CheckCircle2 size={16} strokeWidth={2} color={tokens.success} /> {toast.msg}
        </div>
      ) : (
        <div role="alert" style={{
          position: 'fixed', top: '12vh', left: '50%', transform: 'translateX(-50%)',
          background: toast.tipo === 'error' ? tokens.danger : tokens.warning, color: '#fff',
          padding: '16px 24px', borderRadius: 14, maxWidth: 'min(560px, 92vw)',
          fontSize: 16, fontWeight: 700, fontFamily: PP, zIndex: 1700, lineHeight: 1.3,
          boxShadow: '0 14px 40px rgba(0,0,0,0.30)', display: 'inline-flex', alignItems: 'center', gap: 12,
        }}>
          <AlertTriangle size={24} strokeWidth={2.2} color="#fff" style={{ flexShrink: 0 }} /> {toast.msg}
        </div>
      ))}

      {/* FICHA-PRODUCTO: capa con la ficha completa del producto encima del editor.
          El cotizador sigue montado detras (no se desmonta) -> al cerrar se vuelve a
          la cotizacion EXACTA, en el mismo punto, sin perder scroll ni estado. */}
      {fichaUnico && (
        <div key={fichaUnico} style={{
          // key={fichaUnico}: al abrir otro producto dentro de la capa (similares/
          // relacionados) el contenedor se remonta -> scroll arriba y ficha limpia.
          position: 'fixed', inset: 0, zIndex: 5000, background: '#fff',
          overflowY: 'auto', overscrollBehavior: 'contain',
          // ANTI-REQUIEBRE: reservar el canal de la barra de scroll con scrollbar-gutter para
          // que el ancho del contenido no cambie cuando la barra aparece/desaparece (el texto no
          // se re-quiebra al scrollear). Con 'auto' NO se fuerza una barra vacia cuando no hace
          // falta (evita el track permanente feo en escritorio con fichas cortas).
          scrollbarGutter: 'stable',
        }}>
          <PaginaProducto overlay={{
            idUnico: fichaUnico,
            label: (cabecera?.folio || nombre)
              ? `Volver a la cotizacion ${cabecera?.folio || nombre}`
              : 'Volver a la cotizacion',
            onVolver: () => setFichaUnico(null),
            onIrAProducto: (idu) => setFichaUnico(idu),
          }} />
        </div>
      )}
    </div>
  );
}

// Renglones que se RESTAN (resta) van en ROJO con su signo negativo.
function Casc({ label, valor, fuerte, resta, sangria }) {
  const color = resta ? tokens.danger : COLOR_OSCURO;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '4px 0', paddingLeft: sangria ? 14 : 0 }}>
      <span style={{ fontSize: fuerte ? 14 : 13, color, fontWeight: fuerte ? 700 : 500 }}>{label}</span>
      <span style={{ fontSize: fuerte ? 15 : 13, fontWeight: fuerte ? 800 : 600, color }}>{valor}</span>
    </div>
  );
}

// Miniatura cuadrada del articulo (reusada en tabla y tarjetas movil).
function Thumb({ url, size = 44 }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: 8, background: COLOR_FONDO, flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
    }}>
      {url
        ? <img src={url} alt="" loading="lazy" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} onError={(e) => { e.target.style.display = 'none'; }} />
        : <Package size={20} strokeWidth={1.5} color={COLOR_GRIS_CLARO} />}
    </div>
  );
}

// Fila "etiqueta izquierda / control derecha" para la vista movil.
function FilaControl({ label, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
      <span style={{ fontSize: 13, fontWeight: 600, color: COLOR_GRIS }}>{label}</span>
      <div>{children}</div>
    </div>
  );
}

// Stepper tactil de cantidad (- N +) para la vista movil. Usa los mismos handlers.
function Stepper({ valor, onMenos, onMas, onSet }) {
  const n = Math.max(1, Math.round(Number(valor) || 1));
  const btn = {
    width: 40, height: 40, minWidth: 40, fontSize: 18, fontWeight: 700, fontFamily: PP,
    color: COLOR_OSCURO, background: tokens.surfaceSubtle, border: `1px solid ${COLOR_BORDE}`,
    borderRadius: 8, cursor: 'pointer', touchAction: 'manipulation',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <button type="button" aria-label="Disminuir cantidad" onClick={onMenos} style={btn}>-</button>
      <input
        type="number" min="1" step="1" aria-label="Cantidad" value={n}
        onChange={(e) => onSet(e.target.value)}
        style={{
          width: 56, textAlign: 'center', padding: '8px 6px', border: `1px solid ${COLOR_BORDE}`,
          borderRadius: 8, background: '#fff', fontSize: 15, fontWeight: 700, fontFamily: PP,
          color: COLOR_OSCURO, outline: 'none', MozAppearance: 'textfield',
        }}
      />
      <button type="button" aria-label="Aumentar cantidad" onClick={onMas} style={btn}>+</button>
    </div>
  );
}

// InputPrecio e InputMargen viven ahora en components/LineaPrecioEditable.js
// (importados arriba) — mismo codigo, reusado por el precotizador y por Colecciones.

// COTIZACIONES-HISTORIAL: modal con el historial de versiones de la cotizacion.
// Carga la lista (metadatos) al abrir; cada version se puede Restaurar (carga en el
// editor sin guardar). La mas reciente arriba, marcada "actual". Responsive + cierra
// con X / clic fuera.
function ModalHistorial({ abierto, cotizacionId, onCerrar, onRestaurar, formatear }) {
  const [versiones, setVersiones] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!abierto || !cotizacionId) return;
    let activo = true;
    setCargando(true);
    setError(null);
    fetch(`${API_BASE_CLI}/api/cotizaciones/${cotizacionId}/versiones`, { headers: authHeaders() })
      .then(x => x.json())
      .then(r => {
        if (!activo) return;
        if (r && r.ok) setVersiones(Array.isArray(r.versiones) ? r.versiones : []);
        else setError((r && r.error) || 'No se pudo cargar el historial');
      })
      .catch(() => activo && setError('Sin conexion'))
      .finally(() => activo && setCargando(false));
    return () => { activo = false; };
  }, [abierto, cotizacionId]);

  if (!abierto) return null;

  const fmtFechaHora = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString('es-MX', {
        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    } catch { return '—'; }
  };
  // La fila mas reciente (version_num mayor) es la "actual".
  const maxNum = versiones.reduce((acc, v) => Math.max(acc, Number(v.version_num) || 0), 0);

  return (
    <div
      onClick={onCerrar}
      style={{
        position: 'fixed', inset: 0, zIndex: 1700, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '4vh 12px', overflowY: 'auto',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 14, width: 'min(560px, 96vw)', maxHeight: 'calc(var(--beep-vh) * 92)',
          display: 'flex', flexDirection: 'column', fontFamily: PP,
          boxShadow: '0 16px 48px rgba(0,0,0,0.25)', overflow: 'hidden',
        }}
      >
        {/* Cabecera */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: `1px solid ${COLOR_BORDE}` }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 700, color: COLOR_OSCURO }}>
            <Clock size={17} strokeWidth={2} /> Historial de versiones
          </div>
          <button onClick={onCerrar} aria-label="Cerrar" style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: COLOR_GRIS_CLARO, padding: 6, minWidth: 40, minHeight: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <X size={22} strokeWidth={2} />
          </button>
        </div>

        {/* Lista */}
        <div style={{ overflowY: 'auto', padding: '10px 12px 14px' }}>
          {cargando && <div style={{ padding: 16, color: COLOR_GRIS_CLARO, fontSize: 13 }}>Cargando historial...</div>}
          {!cargando && error && <div style={{ padding: 16, color: tokens.danger, fontSize: 13 }}>{error}</div>}
          {!cargando && !error && versiones.length === 0 && (
            <div style={{ padding: 20, textAlign: 'center', color: COLOR_GRIS_CLARO, fontSize: 13 }}>Aun no hay versiones.</div>
          )}
          {!cargando && !error && versiones.map((v) => {
            const num = Number(v.version_num) || 0;
            const esActual = num === maxNum;
            return (
              <div
                key={num}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
                  padding: '12px 12px', borderRadius: 12, marginBottom: 8,
                  border: `1px solid ${esActual ? tokens.primary : COLOR_BORDE}`,
                  background: esActual ? tokens.primarySoft : '#fff',
                }}
              >
                <span style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  minWidth: 38, height: 30, padding: '0 9px', borderRadius: 8,
                  background: tokens.primary100, color: tokens.primary, fontSize: 14, fontWeight: 800,
                }}>#{num}</span>
                <div style={{ flex: '1 1 160px', minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: COLOR_OSCURO }}>
                    {formatear ? formatear(v.total) : `$${Number(v.total) || 0}`}
                    {esActual && (
                      <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 800, color: tokens.primary, textTransform: 'uppercase', letterSpacing: 0.4 }}>Actual</span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 2 }}>
                    {fmtFechaHora(v.creado_at)}{v.vendedor_nombre ? ` · ${v.vendedor_nombre}` : ''}
                  </div>
                </div>
                <button
                  onClick={() => onRestaurar(num)}
                  style={{
                    ...estiloBotonSecundario, padding: '8px 12px', minHeight: 40,
                    display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0,
                  }}
                ><RotateCcw size={14} strokeWidth={2} /> Restaurar</button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
